import type { PageState, SectionState, BlockState, SiteSettings } from '../store/editor.store'
import {
  getGlassmorphPageHtml,
  getSwissLandingPageHtml, getBrutalLandingPageHtml, getSerifLandingPageHtml,
  getCinemaLandingPageHtml, getMaximalLandingPageHtml, getY2kLandingPageHtml,
  getOrganicLandingPageHtml, getFilmLandingPageHtml, getBentoLandingPageHtml,
  getBentoPortfolioPageHtml, getCinemaPortfolioPageHtml, getFilmPortfolioPageHtml,
  getMinimalPortfolioPageHtml, getOrganicPortfolioPageHtml,
  getSwissPortfolioPageHtml, getBrutalPortfolioPageHtml, getImmersivePortfolioPageHtml,
  getNeoPortfolioPageHtml, studioPageHtml,
} from '@bst/blocks'

// ── Block → HTML ───────────────────────────────────────────────────────────────

let _pageContext: PageState | null = null
let _siteId: string | null = null

function blockHtml(block: BlockState): string {
  const page = _pageContext!
  const d = block.data as Record<string, unknown>

  switch (block.type) {
    case 'heading': {
      const tag = String(d.level ?? 'h1')
      const align = String(d.align ?? 'center')
      return `<div style="text-align:${align};padding:16px 24px"><${tag} style="margin:0;line-height:1.2">${esc(String(d.text ?? 'Заголовок'))}</${tag}></div>`
    }
    case 'paragraph': {
      const align = String(d.align ?? 'left')
      return `<div style="padding:12px 24px;text-align:${align}"><p style="font-size:16px;line-height:1.7;color:#555;margin:0">${esc(String(d.text ?? ''))}</p></div>`
    }
    case 'button': {
      const variant = String(d.variant ?? 'primary')
      const align = String(d.align ?? 'center')
      const styles: Record<string, string> = {
        primary: 'background:#5b6af0;color:#fff;border:none',
        secondary: 'background:#1f1f1f;color:#fff;border:none',
        outline: 'background:transparent;color:#5b6af0;border:2px solid #5b6af0',
        ghost: 'background:transparent;color:#333;border:none',
      }
      const url = String(d.url ?? '#')
      return `<div style="padding:16px 24px;text-align:${align}"><a href="${url}" style="display:inline-block;padding:12px 28px;font-size:15px;font-weight:600;border-radius:8px;text-decoration:none;${styles[variant] ?? styles.primary}">${esc(String(d.text ?? 'Нажмите'))}</a></div>`
    }
    case 'hero-centered': {
      const pb = d.primaryButton as { text: string; url?: string } | undefined
      const sb = d.secondaryButton as { text: string; url?: string } | undefined
      return `<div style="padding:80px 40px;text-align:center;background:linear-gradient(135deg,#0d0d0d,#1a1a2e);color:#fff;min-height:400px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:24px">
  <h1 style="font-size:52px;font-weight:800;line-height:1.15;letter-spacing:-1px;white-space:pre-line;margin:0">${esc(String(d.title ?? ''))}</h1>
  <p style="font-size:18px;color:#aaa;max-width:560px;line-height:1.6;margin:0">${esc(String(d.subtitle ?? ''))}</p>
  <div style="display:flex;gap:12px;margin-top:8px;flex-wrap:wrap;justify-content:center">
    ${pb ? `<a href="${pb.url ?? '#'}" style="padding:13px 28px;font-size:15px;font-weight:600;background:#5b6af0;color:#fff;border:none;border-radius:8px;text-decoration:none">${esc(pb.text)}</a>` : ''}
    ${sb ? `<a href="${sb.url ?? '#'}" style="padding:13px 28px;font-size:15px;font-weight:600;background:transparent;color:#fff;border:1px solid rgba(255,255,255,0.2);border-radius:8px;text-decoration:none">${esc(sb.text)}</a>` : ''}
  </div>
</div>`
    }
    case 'image': {
      const { src = '', alt = '', caption = '', objectFit = 'cover', aspectRatio = '16/9' } = d as Record<string, string>
      return `<div style="padding:8px 24px">
  <div style="aspect-ratio:${aspectRatio};border-radius:8px;overflow:hidden;${src ? '' : 'background:#f0f0f0;display:flex;align-items:center;justify-content:center'}">
    ${src ? `<img src="${src}" alt="${esc(alt)}" style="width:100%;height:100%;object-fit:${objectFit}">` : '<span style="color:#999">Изображение</span>'}
  </div>
  ${caption ? `<p style="font-size:12px;color:#888;text-align:center;margin-top:8px">${esc(caption)}</p>` : ''}
</div>`
    }
    case 'video': {
      const { url = '', aspectRatio = '16/9' } = d as Record<string, string>
      if (!url) return `<div style="padding:8px 24px"><div style="aspect-ratio:${aspectRatio};background:#f0f0f0;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#999">Видео</div></div>`
      let embedUrl = url
      if (url.includes('youtube.com/watch')) {
        const id = new URL(url).searchParams.get('v')
        if (id) embedUrl = `https://www.youtube.com/embed/${id}`
      } else if (url.includes('youtu.be/')) {
        const id = url.split('youtu.be/')[1]?.split('?')[0]
        if (id) embedUrl = `https://www.youtube.com/embed/${id}`
      } else if (url.includes('vimeo.com/')) {
        const id = url.split('vimeo.com/')[1]?.split('?')[0]
        if (id) embedUrl = `https://player.vimeo.com/video/${id}`
      }
      return `<div style="padding:8px 24px"><div style="aspect-ratio:${aspectRatio};border-radius:8px;overflow:hidden"><iframe src="${embedUrl}" style="width:100%;height:100%;border:none" allowfullscreen loading="lazy"></iframe></div></div>`
    }
    case 'features': {
      const items = (d.items as Array<{ icon: string; title: string; description: string }> | undefined) ?? []
      const cols = Number(d.columns ?? 3)
      return `<div style="padding:60px 40px;background:#fff">
  <div style="text-align:center;margin-bottom:48px">
    <h2 style="font-size:36px;font-weight:700;color:#1a1a1a;margin-bottom:12px">${esc(String(d.title ?? ''))}</h2>
    <p style="font-size:16px;color:#666;max-width:560px;margin:0 auto">${esc(String(d.subtitle ?? ''))}</p>
  </div>
  <div style="display:grid;grid-template-columns:repeat(${Math.min(cols, items.length || cols)},1fr);gap:32px;max-width:960px;margin:0 auto">
    ${items.map(item => `<div style="text-align:center"><div style="font-size:36px;margin-bottom:16px">${item.icon}</div><h3 style="font-size:18px;font-weight:600;color:#1a1a1a;margin-bottom:8px">${esc(item.title)}</h3><p style="font-size:14px;color:#666;line-height:1.6;margin:0">${esc(item.description)}</p></div>`).join('\n')}
  </div>
</div>`
    }
    case 'testimonials': {
      const items = (d.items as Array<{ name: string; role: string; text: string }> | undefined) ?? []
      return `<div style="padding:60px 40px;background:#f9f9f9">
  <h2 style="text-align:center;font-size:32px;font-weight:700;color:#1a1a1a;margin-bottom:40px">${esc(String(d.title ?? ''))}</h2>
  <div style="display:grid;grid-template-columns:repeat(${Math.min(3, items.length || 3)},1fr);gap:24px;max-width:960px;margin:0 auto">
    ${items.map(r => `<div style="background:#fff;border-radius:12px;padding:24px;box-shadow:0 2px 12px rgba(0,0,0,0.06)"><p style="font-size:14px;color:#444;line-height:1.7;margin-bottom:20px">"${esc(r.text)}"</p><div style="display:flex;align-items:center;gap:10px"><div style="width:36px;height:36px;border-radius:50%;background:#e8e8e8;display:flex;align-items:center;justify-content:center;font-weight:600;color:#666;flex-shrink:0">${esc(r.name[0] ?? '')}</div><div><div style="font-size:13px;font-weight:600;color:#1a1a1a">${esc(r.name)}</div><div style="font-size:11px;color:#888">${esc(r.role)}</div></div></div></div>`).join('\n')}
  </div>
</div>`
    }
    case 'faq': {
      const items = (d.items as Array<{ question: string; answer: string }> | undefined) ?? []
      return `<div style="padding:60px 40px;max-width:720px;margin:0 auto">
  <h2 style="font-size:32px;font-weight:700;color:#1a1a1a;margin-bottom:32px;text-align:center">${esc(String(d.title ?? ''))}</h2>
  <div class="bst-faq">
    ${items.map((item, i) => `<div class="bst-faq-item" style="border:1px solid #e8e8e8;border-radius:10px;overflow:hidden;margin-bottom:12px">
      <button class="bst-faq-q" onclick="bstFaqToggle(this)" style="width:100%;padding:16px 20px;display:flex;justify-content:space-between;align-items:center;background:#fff;border:none;cursor:pointer;text-align:left">
        <span style="font-size:15px;font-weight:500;color:#1a1a1a">${esc(item.question)}</span>
        <span style="color:#888;font-size:18px;transition:transform 0.2s;display:inline-block">+</span>
      </button>
      <div class="bst-faq-a" style="${i === 0 ? '' : 'display:none;'}padding:0 20px 16px;font-size:14px;color:#555;line-height:1.7">${esc(item.answer)}</div>
    </div>`).join('\n')}
  </div>
</div>`
    }
    case 'pricing': {
      const plans = (d.plans as Array<{ name: string; price: string; period: string; features: string[]; popular: boolean; buttonText: string; buttonUrl?: string }> | undefined) ?? []
      return `<div style="padding:60px 40px;background:#f9f9f9">
  <h2 style="text-align:center;font-size:36px;font-weight:700;color:#1a1a1a;margin-bottom:48px">${esc(String(d.title ?? ''))}</h2>
  <div style="display:grid;grid-template-columns:repeat(${Math.min(3, plans.length || 3)},1fr);gap:24px;max-width:960px;margin:0 auto">
    ${plans.map(plan => `<div style="background:#fff;border-radius:16px;padding:32px 28px;border:${plan.popular ? '2px solid #5b6af0' : '1px solid #e8e8e8'};position:relative">
      ${plan.popular ? '<div style="position:absolute;top:-12px;left:50%;transform:translateX(-50%);background:#5b6af0;color:#fff;font-size:11px;font-weight:600;padding:4px 12px;border-radius:20px">Популярный</div>' : ''}
      <div style="font-size:14px;font-weight:500;color:#888;margin-bottom:8px">${esc(plan.name)}</div>
      <div style="display:flex;align-items:baseline;gap:4px;margin-bottom:24px"><span style="font-size:40px;font-weight:800;color:#1a1a1a">${esc(plan.price)} ₽</span><span style="font-size:13px;color:#888">/${esc(plan.period)}</span></div>
      <ul style="list-style:none;padding:0;margin:0 0 28px;display:flex;flex-direction:column;gap:10px">
        ${(plan.features || []).map(f => `<li style="font-size:13px;color:#444;display:flex;gap:8px;align-items:center"><span style="color:#5b6af0">✓</span> ${esc(f)}</li>`).join('')}
      </ul>
      <a href="${plan.buttonUrl ?? '#'}" style="display:block;padding:11px;border-radius:8px;font-size:13px;font-weight:600;text-align:center;text-decoration:none;background:${plan.popular ? '#5b6af0' : 'transparent'};color:${plan.popular ? '#fff' : '#5b6af0'};border:${plan.popular ? 'none' : '1px solid #5b6af0'}">${esc(plan.buttonText)}</a>
    </div>`).join('\n')}
  </div>
</div>`
    }
    case 'cta': {
      const bg = String(d.background ?? '#5b6af0')
      const isDark = bg.startsWith('#0') || bg.startsWith('#1') || bg === '#5b6af0'
      return `<div style="padding:80px 40px;background:${bg};text-align:center">
  <h2 style="font-size:40px;font-weight:800;color:${isDark ? '#fff' : '#1a1a1a'};margin-bottom:16px;line-height:1.2">${esc(String(d.title ?? ''))}</h2>
  <p style="font-size:18px;color:${isDark ? 'rgba(255,255,255,0.75)' : '#666'};margin-bottom:36px;max-width:500px;margin-left:auto;margin-right:auto">${esc(String(d.subtitle ?? ''))}</p>
  <div style="display:flex;gap:12px;justify-content:center;flex-wrap:wrap">
    <a href="${String(d.primaryUrl ?? '#')}" style="padding:14px 32px;font-size:15px;font-weight:600;background:${isDark ? '#fff' : '#5b6af0'};color:${isDark ? '#5b6af0' : '#fff'};border:none;border-radius:8px;text-decoration:none">${esc(String(d.primaryText ?? ''))}</a>
    ${d.secondaryText ? `<a href="${String(d.secondaryUrl ?? '#')}" style="padding:14px 32px;font-size:15px;font-weight:600;background:transparent;color:${isDark ? 'rgba(255,255,255,0.8)' : '#5b6af0'};border:1px solid ${isDark ? 'rgba(255,255,255,0.3)' : '#5b6af0'};border-radius:8px;text-decoration:none">${esc(String(d.secondaryText))}</a>` : ''}
  </div>
</div>`
    }
    case 'quote': {
      const { text = '', author = '', source = '' } = d as Record<string, string>
      return `<div style="padding:32px 40px"><blockquote style="border-left:4px solid #5b6af0;padding-left:24px;margin:0"><p style="font-size:20px;font-style:italic;color:#1a1a1a;line-height:1.6;margin-bottom:${author ? '16px' : '0'}">"${esc(text)}"</p>${author ? `<footer style="font-size:14px;color:#888">— ${esc(author)}${source ? `, <cite style="font-style:normal">${esc(source)}</cite>` : ''}</footer>` : ''}</blockquote></div>`
    }
    case 'list': {
      const items = (d.items as string[] | undefined) ?? []
      const Tag = d.ordered ? 'ol' : 'ul'
      return `<div style="padding:12px 40px"><${Tag} style="padding-left:20px;display:flex;flex-direction:column;gap:8px">${items.map(item => `<li style="font-size:16px;color:#333;line-height:1.6">${esc(item)}</li>`).join('')}</${Tag}></div>`
    }
    case 'divider': {
      const { style: ls = 'solid', color = '#e0e0e0', width = 100, thickness = 1 } = d as Record<string, string | number>
      return `<div style="padding:16px 24px"><hr style="border:none;border-top:${thickness}px ${ls} ${color};width:${width}%;margin:0 auto"></div>`
    }
    case 'spacer': {
      return `<div style="height:${Number(d.height ?? 60)}px"></div>`
    }
    case 'gallery': {
      const images = (d.images as Array<{ src: string; alt: string }> | undefined) ?? []
      const cols = Number(d.columns ?? 3)
      const gap = Number(d.gap ?? 12)
      return `<div style="padding:8px 24px"><div style="display:grid;grid-template-columns:repeat(${cols},1fr);gap:${gap}px">${images.map(img => `<div style="aspect-ratio:4/3;border-radius:6px;overflow:hidden">${img.src ? `<img src="${img.src}" alt="${esc(img.alt ?? '')}" style="width:100%;height:100%;object-fit:cover">` : '<div style="width:100%;height:100%;background:#f0f0f0;display:flex;align-items:center;justify-content:center;color:#ccc">🖼</div>'}</div>`).join('')}</div></div>`
    }
    case 'team': {
      const members = (d.members as Array<{ name: string; role: string; photo: string; bio: string }> | undefined) ?? []
      return `<div style="padding:60px 40px;background:#fff">
  <h2 style="text-align:center;font-size:32px;font-weight:700;color:#1a1a1a;margin-bottom:40px">${esc(String(d.title ?? ''))}</h2>
  <div style="display:grid;grid-template-columns:repeat(${Math.min(4, members.length || 3)},1fr);gap:32px;max-width:960px;margin:0 auto">
    ${members.map(m => `<div style="text-align:center"><div style="width:80px;height:80px;border-radius:50%;background:#f0f0f0;margin:0 auto 16px;overflow:hidden;display:flex;align-items:center;justify-content:center;font-size:28px;font-weight:700;color:#bbb;border:2px solid #e8e8e8">${m.photo ? `<img src="${m.photo}" alt="${esc(m.name)}" style="width:100%;height:100%;object-fit:cover">` : esc(m.name[0] ?? '')}</div><div style="font-weight:600;font-size:15px;color:#1a1a1a;margin-bottom:4px">${esc(m.name)}</div><div style="font-size:12px;color:#5b6af0;margin-bottom:8px">${esc(m.role)}</div><div style="font-size:13px;color:#666;line-height:1.5">${esc(m.bio)}</div></div>`).join('\n')}
  </div>
</div>`
    }
    case 'navbar': {
      const links = (d.links as Array<{ label: string; url: string }> | undefined) ?? []
      const transparent = Boolean(d.transparent)
      const sticky = Boolean(d.sticky ?? true)
      return `<nav style="padding:0 32px;height:60px;display:flex;align-items:center;justify-content:space-between;background:${transparent ? 'transparent' : '#fff'};border-bottom:${transparent ? 'none' : '1px solid #f0f0f0'};${sticky ? 'position:sticky;top:0;z-index:100;' : ''}box-shadow:${sticky ? '0 2px 8px rgba(0,0,0,0.06)' : 'none'}">
  <div style="font-weight:800;font-size:20px;letter-spacing:-0.5px;color:#1a1a1a">${esc(String(d.logo ?? 'BST'))}</div>
  <div style="display:flex;gap:28px">${links.map(l => `<a href="${l.url}" style="font-size:14px;color:#555;text-decoration:none">${esc(l.label)}</a>`).join('')}</div>
  ${d.ctaText ? `<a href="${String(d.ctaUrl ?? '#')}" style="padding:8px 20px;background:#5b6af0;color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:600;text-decoration:none">${esc(String(d.ctaText))}</a>` : ''}
</nav>`
    }
    case 'footer': {
      const columns = (d.columns as Array<{ title: string; links: Array<{ label: string; url?: string }> }> | undefined) ?? []
      return `<footer style="padding:48px 40px 24px;background:#111;color:#fff">
  <div style="display:grid;grid-template-columns:2fr repeat(${columns.length},1fr);gap:40px;margin-bottom:40px">
    <div><div style="font-weight:800;font-size:22px;letter-spacing:-0.5px;margin-bottom:8px">${esc(String(d.logo ?? 'BST'))}</div><p style="font-size:13px;color:#888;line-height:1.6;margin:0">${esc(String(d.tagline ?? ''))}</p></div>
    ${columns.map(col => `<div><div style="font-weight:600;font-size:12px;color:#aaa;text-transform:uppercase;letter-spacing:1px;margin-bottom:16px">${esc(col.title)}</div><div style="display:flex;flex-direction:column;gap:10px">${col.links.map(l => `<a href="${l.url ?? '#'}" style="font-size:13px;color:#666;text-decoration:none">${esc(l.label)}</a>`).join('')}</div></div>`).join('\n')}
  </div>
  <div style="border-top:1px solid #222;padding-top:20px;font-size:12px;color:#555">${esc(String(d.copyright ?? ''))}</div>
</footer>`
    }
    case 'stats': {
      const items = (d.items as Array<{ value: string; label: string; prefix: string; suffix: string }> | undefined) ?? []
      return `<div style="padding:60px 40px;background:#fff">
  ${d.title ? `<h2 style="text-align:center;font-size:32px;font-weight:700;color:#1a1a1a;margin-bottom:40px">${esc(String(d.title))}</h2>` : ''}
  <div style="display:grid;grid-template-columns:repeat(${Math.min(4, items.length || 4)},1fr);gap:24px;max-width:960px;margin:0 auto">
    ${items.map(s => `<div style="text-align:center"><div style="font-size:48px;font-weight:800;color:#5b6af0;line-height:1;margin-bottom:8px">${esc(s.prefix ?? '')}${esc(s.value)}${esc(s.suffix ?? '')}</div><div style="font-size:14px;color:#888;font-weight:500">${esc(s.label)}</div></div>`).join('\n')}
  </div>
</div>`
    }
    case 'tabs': {
      const tabs = (d.tabs as Array<{ label: string; content: string }> | undefined) ?? []
      return `<div style="padding:24px">
  <div style="display:flex;border-bottom:2px solid #e8e8e8;margin-bottom:24px" role="tablist">
    ${tabs.map((tab, i) => `<button onclick="bstTabSwitch(this,${i})" role="tab" class="bst-tab${i === 0 ? ' bst-tab-active' : ''}" style="padding:10px 20px;font-size:14px;font-weight:${i === 0 ? 600 : 400};color:${i === 0 ? '#5b6af0' : '#888'};border:none;border-bottom:2px solid ${i === 0 ? '#5b6af0' : 'transparent'};background:none;cursor:pointer;margin-bottom:-2px;white-space:nowrap">${esc(tab.label)}</button>`).join('')}
  </div>
  ${tabs.map((tab, i) => `<div class="bst-tab-panel" style="${i !== 0 ? 'display:none;' : ''}font-size:14px;color:#555;line-height:1.7">${esc(tab.content)}</div>`).join('\n')}
</div>`
    }
    case 'map': {
      const { address = '', height = 400, zoom = 14 } = d as Record<string, string | number>
      const query = encodeURIComponent(String(address))
      return `<div style="padding:8px 24px"><div style="height:${height}px;border-radius:8px;overflow:hidden"><iframe src="https://maps.google.com/maps?q=${query}&z=${zoom}&output=embed" style="width:100%;height:100%;border:none" loading="lazy" allowfullscreen></iframe></div></div>`
    }
    case 'social-links': {
      const links = (d.links as Array<{ network: string; url: string }> | undefined) ?? []
      const align = String(d.align ?? 'center')
      const sz = Number(d.size ?? 40)
      const icons: Record<string, string> = { telegram: '✈', vk: 'VK', instagram: '◉', youtube: '▶', twitter: 'X', facebook: 'f', github: '⌥', whatsapp: '✆', tiktok: '♪', linkedin: 'in' }
      return `<div style="padding:24px;text-align:${align}">
  ${d.title ? `<div style="font-size:13px;color:#888;margin-bottom:12px">${esc(String(d.title))}</div>` : ''}
  <div style="display:inline-flex;gap:10px;flex-wrap:wrap;justify-content:${align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'}">
    ${links.map(l => `<a href="${l.url}" target="_blank" rel="noopener" style="width:${sz}px;height:${sz}px;border-radius:50%;background:#f0f0f0;display:inline-flex;align-items:center;justify-content:center;font-size:${sz > 32 ? 14 : 11}px;font-weight:700;color:#555;text-decoration:none;border:1px solid #e0e0e0">${icons[l.network] ?? l.network[0]?.toUpperCase() ?? '?'}</a>`).join('')}
  </div>
</div>`
    }
    case 'accordion': {
      const items = (d.items as Array<{ title: string; content: string }> | undefined) ?? []
      return `<div style="padding:24px 40px">
  <div class="bst-accordion">
    ${items.map((item, i) => `<div style="border:1px solid #e8e8e8;border-radius:8px;overflow:hidden;margin-bottom:8px">
      <button class="bst-acc-btn" onclick="bstAccToggle(this)" style="width:100%;padding:14px 20px;display:flex;justify-content:space-between;align-items:center;background:#fff;border:none;cursor:pointer;text-align:left">
        <span style="font-size:14px;font-weight:500;color:#1a1a1a">${esc(item.title)}</span>
        <span style="color:#5b6af0;font-size:18px;transition:transform 0.2s;display:inline-block">${i === 0 ? '×' : '+'}</span>
      </button>
      <div class="bst-acc-body" style="${i !== 0 ? 'display:none;' : ''}padding:0 20px 14px;font-size:14px;color:#555;line-height:1.7">${esc(item.content)}</div>
    </div>`).join('\n')}
  </div>
</div>`
    }
    case 'countdown': {
      const target = d.targetDate ? new Date(String(d.targetDate)).getTime() : Date.now() + 7 * 24 * 60 * 60 * 1000
      return `<div style="padding:40px 24px;text-align:center;background:#f9f9f9">
  <div id="bst-countdown-${block.id}" style="display:inline-flex;gap:16px" data-target="${target}">
    ${['дней','часов','минут','секунд'].map(label => `<div style="text-align:center"><div class="bst-cd-val" style="font-size:48px;font-weight:800;color:#1a1a1a;line-height:1">--</div><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-top:4px">${label}</div></div>`).join('')}
  </div>
</div>`
    }
    case 'form': {
      const fields = (d.fields as Array<{ type: string; label: string; required: boolean; placeholder: string }> | undefined) ?? [{ type: 'text', label: 'Имя', required: true, placeholder: '' }, { type: 'email', label: 'Email', required: true, placeholder: '' }, { type: 'textarea', label: 'Сообщение', required: false, placeholder: '' }]
      const formId = `form-${block.id}`
      const siteSlug = page.slug?.replace(/^\//, '') || 'site'
      const submitEndpoint = `/api/public/sites/${siteSlug}/forms/${formId}/submit`
      return `<div style="padding:40px 24px;max-width:480px;margin:0 auto">
  <form id="${formId}" onsubmit="bstFormSubmit(event, '${submitEndpoint}', this)" style="display:flex;flex-direction:column;gap:12px">
    ${fields.map(f => `<div><label style="font-size:12px;font-weight:500;color:#555;display:block;margin-bottom:4px">${esc(f.label)}${f.required ? ' *' : ''}</label>${f.type === 'textarea' ? `<textarea name="${esc(f.label)}" placeholder="${esc(f.placeholder ?? '')}" ${f.required ? 'required' : ''} rows="4" style="width:100%;padding:9px 12px;border:1px solid #e0e0e0;border-radius:6px;font-size:14px;resize:vertical;box-sizing:border-box"></textarea>` : `<input type="${f.type}" name="${esc(f.label)}" placeholder="${esc(f.placeholder ?? '')}" ${f.required ? 'required' : ''} style="width:100%;padding:9px 12px;border:1px solid #e0e0e0;border-radius:6px;font-size:14px;box-sizing:border-box">`}</div>`).join('\n')}
    <button type="submit" style="margin-top:8px;padding:11px;font-size:14px;font-weight:600;background:#5b6af0;color:#fff;border:none;border-radius:8px;cursor:pointer">${esc(String(d.submitText ?? 'Отправить'))}</button>
    <div class="bst-form-msg" style="display:none;padding:12px;border-radius:8px;font-size:14px;text-align:center"></div>
  </form>
</div>`
    }
    case 'progress': {
      const items = (d.items as Array<{ label: string; value: number }> | undefined) ?? []
      const color = String(d.color ?? '#5b6af0')
      const h = Number(d.height ?? 8)
      return `<div style="padding:40px">
  ${items.map(bar => {
    const pct = Math.min(100, Math.max(0, Number(bar.value) || 0))
    return `<div style="margin-bottom:20px"><div style="display:flex;justify-content:space-between;margin-bottom:6px"><span style="font-size:14px;font-weight:500;color:#333">${esc(bar.label)}</span>${d.showPercent !== false ? `<span style="font-size:13px;color:#888">${pct}%</span>` : ''}</div><div style="height:${h}px;background:#e8e8e8;border-radius:${h}px;overflow:hidden"><div style="height:100%;width:${pct}%;background:${color};border-radius:${h}px"></div></div></div>`
  }).join('\n')}
</div>`
    }
    case 'timeline': {
      const items = (d.items as Array<{ date: string; title: string; description: string }> | undefined) ?? []
      const accent = String(d.accent ?? '#5b6af0')
      return `<div style="padding:40px">
  ${d.title ? `<h2 style="font-size:28px;font-weight:700;color:#1a1a1a;margin-bottom:32px;text-align:center">${esc(String(d.title))}</h2>` : ''}
  <div style="position:relative;max-width:640px;margin:0 auto">
    <div style="position:absolute;left:79px;top:12px;bottom:12px;width:2px;background:#e8e8e8"></div>
    ${items.map(item => `<div style="display:flex;gap:24px;align-items:flex-start;margin-bottom:28px"><div style="width:60px;text-align:right;flex-shrink:0;padding-top:2px"><span style="font-size:12px;font-weight:600;color:${accent}">${esc(item.date)}</span></div><div style="width:18px;flex-shrink:0;display:flex;justify-content:center;padding-top:4px"><div style="width:12px;height:12px;border-radius:50%;background:${accent};position:relative;z-index:1"></div></div><div><div style="font-size:15px;font-weight:600;color:#1a1a1a;margin-bottom:4px">${esc(item.title)}</div><div style="font-size:13px;color:#666;line-height:1.6">${esc(item.description)}</div></div></div>`).join('\n')}
  </div>
</div>`
    }
    case 'counter-anim': {
      const items = (d.items as Array<{ value: number; label: string; prefix: string; suffix: string }> | undefined) ?? []
      const color = String(d.color ?? '#5b6af0')
      const duration = Number(d.duration ?? 2000)
      return `<div style="padding:60px 40px;background:#f9f9f9">
  <div style="display:grid;grid-template-columns:repeat(${Math.min(4, items.length || 4)},1fr);gap:24px;max-width:960px;margin:0 auto">
    ${items.map((item, i) => `<div style="text-align:center"><div class="bst-counter" data-target="${Number(item.value)}" data-duration="${duration}" style="font-size:52px;font-weight:800;color:${color};line-height:1;margin-bottom:8px" id="bst-ctr-${block.id}-${i}">${item.prefix ?? ''}0${item.suffix ?? ''}</div><div style="font-size:14px;color:#888;font-weight:500">${esc(item.label)}</div></div>`).join('\n')}
  </div>
</div>`
    }
    case 'before-after': {
      const { beforeImage = '', afterImage = '', beforeLabel = 'До', afterLabel = 'После', initialPosition = 50 } = d as Record<string, string | number>
      return `<div style="padding:8px 24px">
  <div class="bst-ba" style="position:relative;aspect-ratio:16/9;border-radius:8px;overflow:hidden;cursor:ew-resize;user-select:none" data-pos="${initialPosition}">
    <img src="${afterImage}" alt="${esc(String(afterLabel))}" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover" onerror="this.style.display='none'">
    <div class="bst-ba-before" style="position:absolute;inset:0;clip-path:inset(0 50% 0 0)">
      <img src="${beforeImage}" alt="${esc(String(beforeLabel))}" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover" onerror="this.style.display='none'">
    </div>
    <div class="bst-ba-handle" style="position:absolute;top:0;bottom:0;left:50%;transform:translateX(-50%);width:2px;background:#fff;box-shadow:0 0 6px rgba(0,0,0,0.4)">
      <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:36px;height:36px;border-radius:50%;background:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;font-size:16px;color:#555">⇔</div>
    </div>
    <div style="position:absolute;top:12px;left:12px;background:rgba(0,0,0,0.5);color:#fff;font-size:11px;font-weight:600;padding:3px 8px;border-radius:4px;pointer-events:none">${esc(String(beforeLabel))}</div>
    <div style="position:absolute;top:12px;right:12px;background:rgba(0,0,0,0.5);color:#fff;font-size:11px;font-weight:600;padding:3px 8px;border-radius:4px;pointer-events:none">${esc(String(afterLabel))}</div>
  </div>
</div>`
    }
    case 'html-embed': {
      const { html = '', caption = '', height = 400 } = d as Record<string, string | number>
      return `<div style="padding:8px 24px"><div style="height:${height}px;border-radius:8px;overflow:hidden;position:relative">${String(html)}</div>${caption ? `<p style="font-size:12px;color:#888;text-align:center;margin-top:8px">${esc(String(caption))}</p>` : ''}</div>`
    }
    case 'image-text': {
      const imgPos = String(d.imagePosition ?? 'left')
      const imgEl = d.image ? `<img src="${String(d.image)}" alt="${esc(String(d.imageAlt ?? ''))}" style="width:100%;border-radius:12px;object-fit:cover;max-height:420px;display:block">` : ''
      const textEl = `<div style="display:flex;flex-direction:column;justify-content:center;gap:16px">${d.tag ? `<span style="font-size:12px;font-weight:600;color:#6366f1;text-transform:uppercase;letter-spacing:0.08em">${esc(String(d.tag))}</span>` : ''}<h2 style="font-size:32px;font-weight:700;color:#111;line-height:1.25;margin:0">${esc(String(d.title ?? ''))}</h2><p style="font-size:16px;color:#555;line-height:1.7;margin:0">${esc(String(d.text ?? ''))}</p>${d.buttonText ? `<a href="${esc(String(d.buttonUrl ?? '#'))}" style="display:inline-flex;align-items:center;background:#6366f1;color:#fff;padding:12px 28px;border-radius:8px;font-weight:600;font-size:15px;text-decoration:none;width:fit-content">${esc(String(d.buttonText))} →</a>` : ''}</div>`
      return `<div style="padding:64px 40px;max-width:1100px;margin:0 auto"><div style="display:grid;grid-template-columns:1fr 1fr;gap:56px;align-items:center">${imgPos === 'left' ? `${imgEl}${textEl}` : `${textEl}${imgEl}`}</div></div>`
    }
    case 'card-grid': {
      const cols = Number(d.columns ?? 3)
      const items = (d.items as Array<Record<string, string>> | undefined) ?? []
      return `<div style="padding:64px 40px"><div style="text-align:center;margin-bottom:48px"><h2 style="font-size:36px;font-weight:700;color:#111;margin-bottom:12px">${esc(String(d.title ?? ''))}</h2>${d.subtitle ? `<p style="font-size:17px;color:#666;max-width:600px;margin:0 auto">${esc(String(d.subtitle))}</p>` : ''}</div><div style="display:grid;grid-template-columns:repeat(${cols},1fr);gap:28px;max-width:1200px;margin:0 auto">${items.map(item => `<div style="background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 16px rgba(0,0,0,0.08)">${item.image ? `<img src="${item.image}" alt="${esc(item.title ?? '')}" style="width:100%;height:200px;object-fit:cover">` : ''}<div style="padding:20px 24px 24px">${item.tag ? `<span style="font-size:11px;font-weight:700;color:#6366f1;text-transform:uppercase;letter-spacing:0.08em">${esc(item.tag)}</span>` : ''}<h3 style="font-size:18px;font-weight:700;color:#111;margin:8px 0 10px">${esc(item.title ?? '')}</h3><p style="font-size:14px;color:#666;line-height:1.6;margin:0 0 16px">${esc(item.text ?? '')}</p>${item.linkText ? `<a href="${esc(item.linkUrl ?? '#')}" style="font-size:14px;font-weight:600;color:#6366f1;text-decoration:none">${esc(item.linkText)} →</a>` : ''}</div></div>`).join('')}</div></div>`
    }
    case 'two-columns': {
      const ratioMap: Record<string, string> = { '50/50': '1fr 1fr', '40/60': '2fr 3fr', '60/40': '3fr 2fr', '33/67': '1fr 2fr', '67/33': '2fr 1fr' }
      const ratio = String(d.ratio ?? '50/50')
      const gap = Number(d.gap ?? 48)
      return `<div style="padding:64px 40px;max-width:1100px;margin:0 auto"><div style="display:grid;grid-template-columns:${ratioMap[ratio] ?? '1fr 1fr'};gap:${gap}px">${d.leftTitle || d.leftText ? `<div>${d.leftTitle ? `<h3 style="font-size:22px;font-weight:700;color:#111;margin-bottom:16px">${esc(String(d.leftTitle))}</h3>` : ''}<p style="font-size:16px;color:#555;line-height:1.7">${esc(String(d.leftText ?? ''))}</p></div>` : '<div></div>'}${d.rightTitle || d.rightText ? `<div>${d.rightTitle ? `<h3 style="font-size:22px;font-weight:700;color:#111;margin-bottom:16px">${esc(String(d.rightTitle))}</h3>` : ''}<p style="font-size:16px;color:#555;line-height:1.7">${esc(String(d.rightText ?? ''))}</p></div>` : '<div></div>'}</div></div>`
    }
    case 'highlight-box': {
      const type = String(d.type ?? 'info')
      const styles: Record<string, { bg: string; border: string; title: string }> = {
        info: { bg: '#eff6ff', border: '#3b82f6', title: '#1d4ed8' },
        success: { bg: '#f0fdf4', border: '#22c55e', title: '#15803d' },
        warning: { bg: '#fffbeb', border: '#f59e0b', title: '#b45309' },
        error: { bg: '#fef2f2', border: '#ef4444', title: '#b91c1c' },
        tip: { bg: '#faf5ff', border: '#a855f7', title: '#7e22ce' },
      }
      const s = styles[type] ?? styles.info
      return `<div style="padding:24px 40px;max-width:800px;margin:0 auto"><div style="display:flex;gap:16px;padding:20px 24px;background:${s.bg};border-left:4px solid ${s.border};border-radius:8px">${d.icon ? `<div style="width:36px;height:36px;border-radius:8px;background:rgba(0,0,0,0.06);display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0">${esc(String(d.icon))}</div>` : ''}<div>${d.title ? `<p style="font-weight:700;color:${s.title};font-size:15px;margin:0 0 6px">${esc(String(d.title))}</p>` : ''}<p style="color:#444;font-size:15px;line-height:1.65;margin:0">${esc(String(d.text ?? ''))}</p></div></div></div>`
    }
    case 'code-block': {
      const code = String(d.code ?? '')
      const lang = String(d.language ?? 'javascript')
      const filename = String(d.filename ?? '')
      const isDark = String(d.theme ?? 'dark') === 'dark'
      const lines = code.split('\n')
      const showNums = d.showLineNumbers !== false
      return `<div style="padding:24px 40px;max-width:900px;margin:0 auto"><div style="border-radius:10px;overflow:hidden;font-family:monospace;box-shadow:0 4px 24px rgba(0,0,0,0.15)"><div style="display:flex;align-items:center;justify-content:space-between;padding:10px 16px;background:${isDark ? '#1e1e1e' : '#e5e7eb'}"><div style="display:flex;gap:16px;align-items:center"><div style="display:flex;gap:6px">${['#ff5f56','#ffbd2e','#27c93f'].map(c => `<div style="width:12px;height:12px;border-radius:50%;background:${c}"></div>`).join('')}</div>${filename ? `<span style="font-size:13px;color:${isDark ? '#aaa' : '#555'}">${esc(filename)}</span>` : ''}<span style="font-size:12px;color:${isDark ? '#666' : '#888'};padding:2px 8px;background:${isDark ? '#333' : '#d1d5db'};border-radius:4px">${lang}</span></div><button onclick="navigator.clipboard.writeText(this.closest('div').nextElementSibling.innerText)" style="font-size:12px;color:${isDark ? '#aaa' : '#555'};background:transparent;border:none;cursor:pointer;padding:4px 8px">⎘ Копировать</button></div><div style="background:${isDark ? '#282c34' : '#f9fafb'};padding:16px;overflow-x:auto">${lines.map((line, i) => `<div style="display:flex;gap:16px;min-height:22px">${showNums ? `<span style="color:${isDark ? '#636d83' : '#9ca3af'};min-width:24px;text-align:right;user-select:none;font-size:13px;line-height:22px">${i + 1}</span>` : ''}<span style="color:${isDark ? '#abb2bf' : '#374151'};font-size:13px;line-height:22px;white-space:pre">${esc(line) || ' '}</span></div>`).join('')}</div></div></div>`
    }
    case 'masonry-gallery': {
      const images = (d.images as Array<{ src: string; alt: string }> | undefined) ?? []
      const cols = Number(d.columns ?? 3)
      const gap = Number(d.gap ?? 12)
      return `<div style="padding:40px"><div style="columns:${cols};column-gap:${gap}px">${images.map(img => `<div style="break-inside:avoid;margin-bottom:${gap}px"><img src="${img.src}" alt="${esc(img.alt ?? '')}" style="width:100%;border-radius:8px;display:block"></div>`).join('')}</div></div>`
    }
    case 'comparison': {
      const features = (d.features as Array<{ name: string; left: string; right: string }> | undefined) ?? []
      const rightH = d.rightHighlight !== false
      return `<div style="padding:64px 40px;max-width:800px;margin:0 auto">${d.title ? `<h2 style="font-size:36px;font-weight:700;color:#111;text-align:center;margin-bottom:40px">${esc(String(d.title))}</h2>` : ''}<div style="border-radius:16px;overflow:hidden;box-shadow:0 4px 32px rgba(0,0,0,0.1)"><div style="display:grid;grid-template-columns:2fr 1fr 1fr;background:#f8f9fa"><div style="padding:16px 24px;font-weight:700;color:#111;font-size:14px">Характеристика</div><div style="padding:16px;font-weight:700;color:#888;font-size:14px;text-align:center">${esc(String(d.leftTitle ?? 'Конкуренты'))}</div><div style="padding:16px;font-weight:700;color:${rightH ? '#6366f1' : '#111'};font-size:14px;text-align:center;background:${rightH ? '#f0f0ff' : 'transparent'}">${esc(String(d.rightTitle ?? 'Мы'))}</div></div>${features.map((f, i) => `<div style="display:grid;grid-template-columns:2fr 1fr 1fr;border-top:1px solid #f0f0f0;background:${i % 2 === 0 ? '#fff' : '#fafafa'}"><div style="padding:14px 24px;color:#333;font-size:14px">${esc(f.name)}</div><div style="padding:14px 16px;color:#888;font-size:14px;text-align:center">${esc(f.left)}</div><div style="padding:14px 16px;color:${rightH ? '#6366f1' : '#333'};font-size:14px;text-align:center;font-weight:600;background:${rightH ? 'rgba(99,102,241,0.04)' : 'transparent'}">${esc(f.right)}</div></div>`).join('')}</div></div>`
    }
    case 'awards': {
      const items = (d.items as Array<{ image: string; name: string; description: string; link: string }> | undefined) ?? []
      return `<div style="padding:64px 40px;text-align:center">${d.title ? `<h2 style="font-size:32px;font-weight:700;color:#111;margin-bottom:40px">${esc(String(d.title))}</h2>` : ''}<div style="display:flex;flex-wrap:wrap;gap:32px;justify-content:center;max-width:1000px;margin:0 auto">${items.map(item => `<a href="${esc(item.link ?? '#')}" style="display:flex;flex-direction:column;align-items:center;gap:10px;text-decoration:none;padding:24px 32px;background:#fff;border-radius:12px;box-shadow:0 2px 12px rgba(0,0,0,0.07);min-width:180px">${item.image ? `<img src="${item.image}" alt="${esc(item.name ?? '')}" style="max-width:120px;max-height:40px;object-fit:contain;filter:grayscale(1);opacity:0.7">` : `<span style="font-size:18px;font-weight:700;color:#333">${esc(item.name ?? '')}</span>`}<span style="font-size:13px;color:#777">${esc(item.description ?? '')}</span></a>`).join('')}</div></div>`
    }
    case 'guarantee-bar': {
      const items = (d.items as Array<{ icon: string; text: string }> | undefined) ?? []
      const bg = String(d.background ?? '#f8f9fa')
      return `<div style="background:${bg};padding:28px 40px"><div style="display:flex;flex-wrap:wrap;gap:24px;justify-content:center;max-width:1000px;margin:0 auto">${items.map(item => `<div style="display:flex;align-items:center;gap:10px"><span style="font-size:22px">${esc(item.icon ?? '')}</span><span style="font-size:14px;font-weight:500;color:#444">${esc(item.text ?? '')}</span></div>`).join('')}</div></div>`
    }
    case 'hero-minimal': {
      const align = String(d.align ?? 'center')
      const bg = String(d.background ?? '#ffffff')
      const titleColor = String(d.titleColor ?? '#0f0f0f')
      const pb = String(d.primaryButton ?? '')
      const sb = String(d.secondaryButton ?? '')
      return `<div style="background:${bg};padding:100px 40px;text-align:${align}"><div style="max-width:760px;margin:${align === 'center' ? '0 auto' : '0'}">${d.label ? `<div style="display:inline-flex;align-items:center;gap:8px;background:rgba(99,102,241,0.1);color:#6366f1;padding:6px 14px;border-radius:100px;font-size:13px;font-weight:600;margin-bottom:32px">✦ ${esc(String(d.label))}</div>` : ''}<h1 style="font-size:72px;font-weight:900;color:${titleColor};line-height:1.05;letter-spacing:-0.03em;margin:0 0 24px;white-space:pre-line">${esc(String(d.title ?? ''))}</h1>${d.subtitle ? `<p style="font-size:20px;color:#666;line-height:1.6;margin-bottom:40px;max-width:560px;margin-left:${align === 'center' ? 'auto' : '0'};margin-right:${align === 'center' ? 'auto' : '0'}">${esc(String(d.subtitle))}</p>` : ''}<div style="display:flex;gap:14px;flex-wrap:wrap;justify-content:${align === 'center' ? 'center' : 'flex-start'}">${pb ? `<a href="#" style="display:inline-flex;align-items:center;background:#0f0f0f;color:#fff;padding:14px 32px;border-radius:8px;font-weight:600;font-size:16px;text-decoration:none">${esc(pb)}</a>` : ''}${sb ? `<a href="#" style="display:inline-flex;align-items:center;gap:8px;color:#333;padding:14px 24px;border-radius:8px;font-weight:600;font-size:16px;text-decoration:none;border:1.5px solid #ddd">${esc(sb)} →</a>` : ''}</div></div></div>`
    }
    case 'testimonials-carousel': {
      const items = (d.items as Array<Record<string, unknown>> | undefined) ?? []
      if (!items.length) return ''
      const first = items[0] as Record<string, unknown>
      return `<div style="padding:64px 40px;background:#fafafa;text-align:center">${d.title ? `<h2 style="font-size:36px;font-weight:700;color:#111;margin-bottom:48px">${esc(String(d.title))}</h2>` : ''}<div style="max-width:680px;margin:0 auto"><div style="display:flex;justify-content:center;gap:4px;margin-bottom:24px">${Array.from({ length: 5 }).map((_, i) => `<span style="font-size:20px;color:${i < Number(first.rating ?? 5) ? '#f59e0b' : '#ddd'}">★</span>`).join('')}</div><p style="font-size:20px;color:#333;line-height:1.7;margin-bottom:32px;font-style:italic">"${esc(String(first.text ?? ''))}"</p><div style="display:flex;align-items:center;justify-content:center;gap:14px">${first.avatar ? `<img src="${String(first.avatar)}" alt="${esc(String(first.name ?? ''))}" style="width:52px;height:52px;border-radius:50%;object-fit:cover">` : ''}<div style="text-align:left"><div style="font-weight:700;color:#111">${esc(String(first.name ?? ''))}</div><div style="font-size:13px;color:#888">${esc(String(first.role ?? ''))}${first.company ? `, ${esc(String(first.company))}` : ''}</div></div></div>${items.length > 1 ? `<p style="font-size:12px;color:#aaa;margin-top:16px">${items.length} отзывов</p>` : ''}</div></div>`
    }
    case 'image-carousel': {
      const images = (d.images as Array<{ src: string; alt: string; caption: string }> | undefined) ?? []
      if (!images.length) return ''
      const aspectMap: Record<string, string> = { '16/9': '56.25%', '4/3': '75%', '1/1': '100%', '21/9': '42.85%', '3/2': '66.67%' }
      const ar = String(d.aspectRatio ?? '16/9')
      return `<div style="padding:40px;max-width:1100px;margin:0 auto"><div class="bst-carousel" style="position:relative"><div style="padding-bottom:${aspectMap[ar] ?? '56.25%'};position:relative;border-radius:12px;overflow:hidden">${images.map((img, i) => `<img src="${img.src}" alt="${esc(img.alt ?? '')}" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transition:opacity 0.3s;opacity:${i === 0 ? 1 : 0}" class="bst-carousel-slide">`).join('')}</div>${d.showDots !== false && images.length > 1 ? `<div style="display:flex;justify-content:center;gap:8px;margin-top:16px">${images.map((_, i) => `<div onclick="bstCarouselGo(this,${i})" style="width:8px;height:8px;border-radius:50%;background:${i === 0 ? '#6366f1' : '#ddd'};cursor:pointer" class="bst-carousel-dot"></div>`).join('')}</div>` : ''}</div></div>`
    }
    case 'product-showcase': {
      const features = (d.features as Array<{ value: string }> | undefined) ?? []
      const imgPos = String(d.imagePosition ?? 'left')
      const imgEl = d.image ? `<div style="position:relative">${d.badge ? `<div style="position:absolute;top:16px;left:16px;z-index:1;background:#ef4444;color:#fff;padding:4px 12px;border-radius:100px;font-size:12px;font-weight:700">${esc(String(d.badge))}</div>` : ''}<img src="${String(d.image)}" alt="${esc(String(d.imageAlt ?? ''))}" style="width:100%;border-radius:16px;object-fit:cover;max-height:480px;display:block"></div>` : ''
      const infoEl = `<div style="display:flex;flex-direction:column;justify-content:center;gap:20px"><h2 style="font-size:34px;font-weight:800;color:#111;margin:0">${esc(String(d.name ?? ''))}</h2><p style="font-size:16px;color:#555;line-height:1.7;margin:0">${esc(String(d.description ?? ''))}</p>${features.length ? `<ul style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:8px">${features.map(f => `<li style="display:flex;align-items:center;gap:10px;font-size:14px;color:#333"><span style="color:#22c55e;font-weight:700">✓</span>${esc(f.value ?? '')}</li>`).join('')}</ul>` : ''}<div style="display:flex;align-items:baseline;gap:12px"><span style="font-size:36px;font-weight:800;color:#111">${esc(String(d.price ?? ''))}</span>${d.oldPrice ? `<span style="font-size:20px;color:#aaa;text-decoration:line-through">${esc(String(d.oldPrice))}</span>` : ''}</div><a href="#" style="display:inline-block;background:#111;color:#fff;padding:16px 36px;border-radius:10px;font-weight:700;font-size:17px;text-decoration:none;width:fit-content">${esc(String(d.buttonText ?? 'В корзину'))}</a></div>`
      return `<div style="padding:64px 40px;max-width:1100px;margin:0 auto"><div style="display:grid;grid-template-columns:1fr 1fr;gap:56px;align-items:center">${imgPos === 'left' ? `${imgEl}${infoEl}` : `${infoEl}${imgEl}`}</div></div>`
    }
    case 'product-grid': {
      const cols = Number(d.columns ?? 3)
      const products = (d.products as Array<Record<string, string>> | undefined) ?? []
      return `<div style="padding:64px 40px">${d.title ? `<h2 style="font-size:36px;font-weight:700;color:#111;text-align:center;margin-bottom:40px">${esc(String(d.title))}</h2>` : ''}<div style="display:grid;grid-template-columns:repeat(${cols},1fr);gap:24px;max-width:1200px;margin:0 auto">${products.map(p => `<div style="background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.07)"><div style="position:relative">${p.badge ? `<div style="position:absolute;top:12px;left:12px;background:#ef4444;color:#fff;padding:3px 10px;border-radius:100px;font-size:11px;font-weight:700">${esc(p.badge)}</div>` : ''}${p.image ? `<img src="${p.image}" alt="${esc(p.name ?? '')}" style="width:100%;height:220px;object-fit:cover">` : ''}</div><div style="padding:16px 20px 20px"><h3 style="font-size:16px;font-weight:700;color:#111;margin:0 0 8px">${esc(p.name ?? '')}</h3><div style="display:flex;align-items:baseline;gap:8px;margin-bottom:14px"><span style="font-size:20px;font-weight:800;color:#111">${esc(p.price ?? '')}</span>${p.oldPrice ? `<span style="font-size:14px;color:#aaa;text-decoration:line-through">${esc(p.oldPrice)}</span>` : ''}</div><a href="#" style="display:block;background:#111;color:#fff;padding:10px;border-radius:8px;font-weight:600;font-size:14px;text-align:center;text-decoration:none">${esc(p.buttonText ?? 'В корзину')}</a></div></div>`).join('')}</div></div>`
    }
    case 'payment-logos': {
      const bg = String(d.background ?? '#f8f9fa')
      const methods = [
        { text: 'VISA', color: '#1A1F71' }, { text: '⬤ MC', color: '#EB001B' },
        { text: 'МИР', color: '#00853e' }, { text: 'СБП', color: '#5b4fcf' },
        { text: 'PayPal', color: '#003087' }, { text: '🍎 Pay', color: '#000' },
        { text: 'G Pay', color: '#4285f4' }, { text: 'Сбер', color: '#21A038' },
      ]
      return `<div style="background:${bg};padding:32px 40px">${d.title ? `<p style="text-align:center;font-size:13px;color:#888;font-weight:500;margin-bottom:16px;text-transform:uppercase;letter-spacing:0.05em">${esc(String(d.title))}</p>` : ''}<div style="display:flex;flex-wrap:wrap;gap:12px;justify-content:center">${methods.map(m => `<div style="display:flex;align-items:center;justify-content:center;padding:8px 18px;background:#fff;border-radius:8px;box-shadow:0 1px 4px rgba(0,0,0,0.1);min-width:80px"><span style="font-size:14px;font-weight:800;color:${m.color}">${m.text}</span></div>`).join('')}</div></div>`
    }
    case 'quiz': {
      const quizId = `quiz-${block.id}`
      const bg = String(d.background ?? '#f0f4ff')
      const accent = String(d.accentColor ?? '#5b6af0')
      const steps = (d.steps as Array<{id:string;question:string;options:string[]}> | undefined) ?? []
      const resultTitle = esc(String(d.resultTitle ?? 'Оставьте контакты'))
      const resultSubtitle = esc(String(d.resultSubtitle ?? ''))
      const submitText = esc(String(d.submitText ?? 'Получить результат'))
      const successText = esc(String(d.successText ?? 'Спасибо! Мы свяжемся с вами.'))
      const page = _pageContext!
      const siteSlug = page.slug?.replace(/^\//, '').split('/')[0] || 'site'
      const submitEndpoint = `/api/public/sites/${siteSlug}/forms/${quizId}/submit`
      const stepsJson = JSON.stringify(steps)
      const totalSteps = steps.length + 1

      return `<div id="${quizId}-wrap" style="background:${bg};padding:64px 24px">
  <div style="max-width:600px;margin:0 auto;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.1)">
    <div id="${quizId}-bar" style="height:4px;background:#f0f0f0"><div id="${quizId}-prog" style="height:100%;width:${100/totalSteps}%;background:${accent};border-radius:2px;transition:width 0.3s"></div></div>
    <div id="${quizId}-body" style="padding:32px">
      <div style="text-align:center;margin-bottom:28px">
        <h2 style="font-size:24px;font-weight:700;color:#111;margin:0 0 8px">${esc(String(d.title ?? 'Квиз'))}</h2>
        ${d.subtitle ? `<p style="font-size:15px;color:#666;margin:0">${esc(String(d.subtitle))}</p>` : ''}
      </div>
      <div id="${quizId}-step"></div>
      <div id="${quizId}-msg" style="display:none;text-align:center;padding:24px 0;font-size:16px;color:#22c55e;font-weight:600">${successText}</div>
    </div>
  </div>
</div>
<script>
(function(){
  var id='${quizId}',steps=${stepsJson},cur=0,answers={};
  var total=${totalSteps};
  var resultTitle='${resultTitle}',resultSubtitle='${resultSubtitle}',submitText='${submitText}';
  var accent='${accent}';
  function render(){
    var body=document.getElementById(id+'-step');
    var prog=document.getElementById(id+'-prog');
    prog.style.width=((cur+1)/total*100)+'%';
    if(cur<steps.length){
      var s=steps[cur];
      body.innerHTML='<p style="font-size:16px;font-weight:600;color:#111;margin-bottom:16px">'+(s.question||'')+'</p><div style="display:flex;flex-direction:column;gap:10px">'
        +s.options.map(function(o){return'<button onclick="bstQuizAnswer(\''+id+'\',\''+s.id+'\',\''+encodeURIComponent(o)+'\')" style="padding:12px 16px;background:#f8f9fa;border:2px solid #f0f0f0;border-radius:8px;font-size:14px;color:#111;text-align:left;cursor:pointer;font-weight:500">'+o+'</button>'}).join('')
        +'</div>'
        +'<div style="margin-top:16px;font-size:11px;color:#aaa;text-align:center">Шаг '+(cur+1)+' из '+total+'</div>';
    } else {
      body.innerHTML='<div style="text-align:center;margin-bottom:20px"><h3 style="font-size:20px;font-weight:700;color:#111;margin:0 0 8px">'+resultTitle+'</h3><p style="font-size:15px;color:#666;margin:0">'+resultSubtitle+'</p></div>'
        +'<form id="'+id+'-form" onsubmit="bstQuizSubmit(event,\''+id+'\',\'${submitEndpoint}\')" style="display:flex;flex-direction:column;gap:10px">'
        +'<input name="name" type="text" placeholder="Ваше имя" required style="padding:10px 14px;border:1px solid #e0e0e0;border-radius:8px;font-size:14px">'
        +'<input name="phone" type="tel" placeholder="+7 (___) ___-__-__" required style="padding:10px 14px;border:1px solid #e0e0e0;border-radius:8px;font-size:14px">'
        +'<button type="submit" style="padding:12px;background:'+accent+';color:#fff;border:none;border-radius:8px;font-size:15px;font-weight:700;cursor:pointer">'+submitText+'</button>'
        +'</form>';
    }
  }
  window.bstQuizAnswer=function(qid,stepId,val){
    if(qid!==id)return;
    answers[stepId]=decodeURIComponent(val);
    cur++;render();
  };
  window.bstQuizSubmit=function(e,qid,url){
    if(qid!==id)return;
    e.preventDefault();
    var fd=new FormData(e.target),data=Object.assign({},answers);
    fd.forEach(function(v,k){data[k]=v});
    fetch(url,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({data:data})})
      .then(function(){
        document.getElementById(qid+'-step').style.display='none';
        document.getElementById(qid+'-msg').style.display='block';
      });
  };
  render();
})();
</script>`
    }
    case 'calculator': {
      const calcId = `calc-${block.id}`
      const bg = String(d.background ?? '#fff')
      const accent = String(d.accentColor ?? '#5b6af0')
      const currency = String(d.currency ?? '₽')
      const basePrice = Number(d.basePrice ?? 0)
      const params = (d.params as Array<{id:string;label:string;type:string;min?:number;max?:number;step?:number;default?:number;options?:string[];multipliers?:number[];multiplier?:number}> | undefined) ?? []
      const resultLabel = esc(String(d.resultLabel ?? 'Ориентировочная стоимость'))
      const ctaText = esc(String(d.ctaText ?? 'Получить расчёт'))
      const ctaUrl = String(d.ctaUrl ?? '#')
      const paramsJson = JSON.stringify(params)

      const paramInputs = params.map(p => {
        if (p.type === 'range') {
          return `<div style="margin-bottom:18px"><label style="display:block;font-size:13px;font-weight:600;color:#555;margin-bottom:6px">${esc(p.label)} <span id="${calcId}-${p.id}-val" style="color:${accent}">${p.default ?? p.min ?? 0}</span></label><input type="range" id="${calcId}-${p.id}" min="${p.min ?? 0}" max="${p.max ?? 100}" step="${p.step ?? 1}" value="${p.default ?? p.min ?? 0}" oninput="bstCalc('${calcId}')" style="width:100%;accent-color:${accent}"></div>`
        } else if (p.type === 'select') {
          return `<div style="margin-bottom:18px"><label style="display:block;font-size:13px;font-weight:600;color:#555;margin-bottom:6px">${esc(p.label)}</label><select id="${calcId}-${p.id}" onchange="bstCalc('${calcId}')" style="width:100%;padding:8px 12px;border-radius:8px;border:1px solid #ddd;font-size:14px">${(p.options ?? []).map((opt, j) => `<option value="${j}">${esc(opt)}</option>`).join('')}</select></div>`
        } else {
          return `<div style="margin-bottom:18px"><label style="display:block;font-size:13px;font-weight:600;color:#555;margin-bottom:6px">${esc(p.label)}</label><div style="display:flex;gap:8px;flex-wrap:wrap">${(p.options ?? []).map((opt, j) => `<label style="cursor:pointer"><input type="radio" name="${calcId}-${p.id}" value="${j}" ${j === (p.default ?? 0) ? 'checked' : ''} onchange="bstCalc('${calcId}')" style="display:none"><span id="${calcId}-${p.id}-opt${j}" style="display:inline-block;padding:7px 14px;border-radius:8px;border:2px solid ${j === (p.default ?? 0) ? accent : '#ddd'};background:${j === (p.default ?? 0) ? accent : '#fff'};color:${j === (p.default ?? 0) ? '#fff' : '#333'};font-size:13px;font-weight:500">${esc(opt)}</span></label>`).join('')}</div></div>`
        }
      }).join('')

      return `<div style="background:${bg};padding:64px 24px">
  <div style="max-width:680px;margin:0 auto">
    <div style="text-align:center;margin-bottom:40px">
      <h2 style="font-size:32px;font-weight:700;color:#111;margin:0 0 10px">${esc(String(d.title ?? 'Калькулятор'))}</h2>
      ${d.subtitle ? `<p style="font-size:16px;color:#666;margin:0">${esc(String(d.subtitle))}</p>` : ''}
    </div>
    <div style="background:#f8f9fa;border-radius:16px;padding:32px;display:flex;gap:32px;flex-wrap:wrap">
      <div style="flex:2 1 280px">${paramInputs}</div>
      <div style="flex:1 1 180px;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#fff;border-radius:12px;padding:24px;box-shadow:0 2px 12px rgba(0,0,0,0.06);min-width:160px">
        <div style="font-size:12px;color:#888;margin-bottom:8px;text-align:center">${resultLabel}</div>
        <div id="${calcId}-total" style="font-size:36px;font-weight:800;color:${accent};line-height:1">${basePrice.toLocaleString('ru')} ${currency}</div>
        <a href="${ctaUrl}" style="display:block;margin-top:20px;padding:10px 18px;background:${accent};color:#fff;border-radius:8px;font-size:13px;font-weight:700;text-decoration:none;text-align:center">${ctaText}</a>
      </div>
    </div>
  </div>
</div>
<script>
(function(){
  var id='${calcId}',base=${basePrice},params=${paramsJson},currency='${currency}';
  window.bstCalc=function(cid){
    if(cid!==id)return;
    var price=base;
    params.forEach(function(p){
      if(p.type==='range'){
        var el=document.getElementById(id+'-'+p.id);
        var v=parseInt(el.value);
        var lbl=document.getElementById(id+'-'+p.id+'-val');
        if(lbl)lbl.textContent=v;
        price+=v*(p.multiplier||0);
      } else if(p.type==='select'){
        var sel=document.getElementById(id+'-'+p.id);
        var idx=parseInt(sel.value);
        price*=(p.multipliers||[1])[idx]||1;
      } else {
        var checked=document.querySelector('input[name="'+id+'-'+p.id+'"]:checked');
        if(checked){
          var idx=parseInt(checked.value);
          price*=(p.multipliers||[1])[idx]||1;
          (p.options||[]).forEach(function(opt,j){
            var span=document.getElementById(id+'-'+p.id+'-opt'+j);
            if(span){
              var active=j===idx;
              span.style.background=active?'${accent}':'#fff';
              span.style.borderColor=active?'${accent}':'#ddd';
              span.style.color=active?'#fff':'#333';
            }
          });
        }
      }
    });
    var el=document.getElementById(id+'-total');
    if(el)el.textContent=Math.round(price).toLocaleString('ru')+' '+currency;
  };
})();
</script>`
    }
    case 'slider': {
      const items = (d.slides as Array<{title?:string;subtitle?:string;image?:string;buttonText?:string;buttonUrl?:string}> ?? [])
      if (!items.length) return '<div style="padding:40px;text-align:center;color:#888">Слайдер пуст</div>'
      const slides = items.map((s, i) => `<div class="bst-slide" style="display:${i === 0 ? 'flex' : 'none'};flex-direction:column;align-items:center;justify-content:center;min-height:320px;padding:60px 40px;text-align:center;gap:16px;${s.image ? `background:linear-gradient(rgba(0,0,0,0.45),rgba(0,0,0,0.45)),url('${s.image}') center/cover` : 'background:#1a1a2e'};color:#fff">
  ${s.title ? `<h2 style="margin:0;font-size:40px;font-weight:800">${esc(s.title)}</h2>` : ''}
  ${s.subtitle ? `<p style="margin:0;font-size:18px;color:rgba(255,255,255,0.8)">${esc(s.subtitle)}</p>` : ''}
  ${s.buttonText ? `<a href="${s.buttonUrl ?? '#'}" style="margin-top:8px;display:inline-block;padding:12px 28px;background:#5b6af0;color:#fff;border-radius:8px;text-decoration:none;font-weight:600">${esc(s.buttonText)}</a>` : ''}
</div>`).join('')
      const dots = items.map((_s, i) => `<button onclick="bstSlider(this,${i})" class="bst-dot" style="width:8px;height:8px;border-radius:50%;border:none;background:${i === 0 ? '#fff' : 'rgba(255,255,255,0.4)'};cursor:pointer;padding:0"></button>`).join('')
      return `<div class="bst-slider" style="position:relative">${slides}<div style="position:absolute;bottom:16px;left:50%;transform:translateX(-50%);display:flex;gap:6px">${dots}</div></div>`
    }
    case 'hero-split': {
      const imagePos = String(d.imagePosition ?? 'right')
      const img = String(d.image ?? '')
      const content = `<div style="flex:1;padding:60px 40px;display:flex;flex-direction:column;justify-content:center;gap:20px">
  <h1 style="margin:0;font-size:44px;font-weight:800;line-height:1.2">${esc(String(d.title ?? ''))}</h1>
  <p style="margin:0;font-size:17px;color:#666;line-height:1.7">${esc(String(d.subtitle ?? ''))}</p>
  ${d.buttonText ? `<div><a href="${String(d.buttonUrl ?? '#')}" style="display:inline-block;padding:13px 28px;background:#5b6af0;color:#fff;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">${esc(String(d.buttonText))}</a></div>` : ''}
</div>`
      const imgDiv = `<div style="flex:1;min-height:360px;overflow:hidden">${img ? `<img src="${img}" style="width:100%;height:100%;object-fit:cover">` : '<div style="width:100%;height:100%;background:#e8ecff;display:flex;align-items:center;justify-content:center;color:#5b6af0;font-size:48px">🖼</div>'}</div>`
      return `<div class="bst-flex-split" style="display:flex;flex-wrap:wrap">${imagePos === 'left' ? imgDiv + content : content + imgDiv}</div>`
    }
    case 'logo-strip': {
      const logos = (d.logos as Array<{url?:string;alt?:string}> ?? [])
      const title = String(d.title ?? '')
      return `<div style="padding:40px;text-align:center">
  ${title ? `<p style="font-size:13px;color:#999;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:24px;font-weight:600">${esc(title)}</p>` : ''}
  <div style="display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:32px;opacity:0.6">
    ${logos.map(l => l.url ? `<img src="${l.url}" alt="${esc(l.alt ?? '')}" style="height:40px;object-fit:contain;filter:grayscale(1)">` : '').join('')}
  </div>
</div>`
    }
    case 'process-steps': {
      const steps = (d.steps as Array<{number?:string;title?:string;description?:string}> ?? [])
      const stepsHtml = steps.map(s => `<div style="flex:1;min-width:200px;text-align:center;padding:24px">
  <div style="width:48px;height:48px;border-radius:50%;background:#5b6af0;color:#fff;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:800;margin:0 auto 16px">${esc(String(s.number ?? ''))}</div>
  <h3 style="margin:0 0 8px;font-size:16px;font-weight:700">${esc(String(s.title ?? ''))}</h3>
  <p style="margin:0;font-size:14px;color:#666;line-height:1.6">${esc(String(s.description ?? ''))}</p>
</div>`).join(`<div style="padding:0 8px;color:#ccc;font-size:24px;align-self:center">→</div>`)
      return `<div style="padding:60px 40px">
  ${d.title ? `<h2 style="text-align:center;margin:0 0 40px;font-size:32px;font-weight:800">${esc(String(d.title))}</h2>` : ''}
  <div style="display:flex;flex-wrap:wrap;justify-content:center;align-items:flex-start">${stepsHtml}</div>
</div>`
    }
    case 'contact-block': {
      return `<div style="padding:60px 40px;max-width:700px;margin:0 auto">
  ${d.title ? `<h2 style="text-align:center;margin:0 0 8px;font-size:32px;font-weight:800">${esc(String(d.title))}</h2>` : ''}
  ${d.subtitle ? `<p style="text-align:center;color:#666;margin-bottom:40px">${esc(String(d.subtitle))}</p>` : ''}
  <form style="display:flex;flex-direction:column;gap:16px">
    <input type="text" placeholder="Имя" style="padding:12px 16px;border:1px solid #ddd;border-radius:8px;font-size:15px">
    <input type="email" placeholder="Email" style="padding:12px 16px;border:1px solid #ddd;border-radius:8px;font-size:15px">
    <input type="tel" placeholder="Телефон" style="padding:12px 16px;border:1px solid #ddd;border-radius:8px;font-size:15px">
    <textarea placeholder="Сообщение" rows="4" style="padding:12px 16px;border:1px solid #ddd;border-radius:8px;font-size:15px;resize:vertical"></textarea>
    <button type="submit" style="padding:13px;background:#5b6af0;color:#fff;border:none;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer">${esc(String(d.buttonText ?? 'Отправить'))}</button>
  </form>
</div>`
    }
    case 'newsletter': {
      return `<div style="padding:60px 40px;text-align:center">
  ${d.title ? `<h2 style="margin:0 0 12px;font-size:28px;font-weight:800">${esc(String(d.title))}</h2>` : ''}
  ${d.subtitle ? `<p style="color:#666;margin-bottom:28px">${esc(String(d.subtitle))}</p>` : ''}
  <form style="display:flex;gap:8px;max-width:440px;margin:0 auto;flex-wrap:wrap;justify-content:center">
    <input type="email" placeholder="Ваш email" style="flex:1;min-width:200px;padding:12px 16px;border:1px solid #ddd;border-radius:8px;font-size:15px">
    <button type="submit" style="padding:12px 24px;background:#5b6af0;color:#fff;border:none;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer;white-space:nowrap">${esc(String(d.buttonText ?? 'Подписаться'))}</button>
  </form>
</div>`
    }
    case 'table': {
      const headers = (d.headers as string[] ?? [])
      const rows = (d.rows as string[][] ?? [])
      return `<div style="padding:24px;overflow-x:auto">
  ${d.title ? `<h3 style="margin:0 0 16px;font-size:20px;font-weight:700">${esc(String(d.title))}</h3>` : ''}
  <table style="width:100%;border-collapse:collapse;font-size:14px">
    ${headers.length ? `<thead><tr>${headers.map(h => `<th style="padding:12px 16px;background:#f5f5f5;border:1px solid #e0e0e0;text-align:left;font-weight:600">${esc(h)}</th>`).join('')}</tr></thead>` : ''}
    <tbody>${rows.map(r => `<tr>${r.map(c => `<td style="padding:12px 16px;border:1px solid #e0e0e0">${esc(c)}</td>`).join('')}</tr>`).join('')}</tbody>
  </table>
</div>`
    }
    case 'icon-features': {
      const items = (d.items as Array<{icon?:string;title?:string;description?:string}> ?? [])
      const cols = parseInt(String(d.columns ?? '3'))
      return `<div style="padding:60px 40px">
  ${d.title ? `<h2 style="text-align:center;margin:0 0 40px;font-size:32px;font-weight:800">${esc(String(d.title))}</h2>` : ''}
  <div style="display:grid;grid-template-columns:repeat(${cols},1fr);gap:32px">
    ${items.map(it => `<div style="text-align:center">
      <div style="font-size:36px;margin-bottom:16px">${it.icon ?? '⭐'}</div>
      <h3 style="margin:0 0 8px;font-size:16px;font-weight:700">${esc(String(it.title ?? ''))}</h3>
      <p style="margin:0;font-size:14px;color:#666;line-height:1.6">${esc(String(it.description ?? ''))}</p>
    </div>`).join('')}
  </div>
</div>`
    }
    case 'banner': {
      const bg = String(d.background ?? '#5b6af0')
      const textColor = String(d.textColor ?? '#fff')
      return `<div style="padding:48px 40px;background:${bg};text-align:center">
  ${d.title ? `<h2 style="margin:0 0 12px;font-size:32px;font-weight:800;color:${textColor}">${esc(String(d.title))}</h2>` : ''}
  ${d.subtitle ? `<p style="margin:0 0 24px;font-size:17px;color:${textColor};opacity:0.85">${esc(String(d.subtitle))}</p>` : ''}
  ${d.buttonText ? `<a href="${String(d.buttonUrl ?? '#')}" style="display:inline-block;padding:13px 28px;background:#fff;color:#333;border-radius:8px;text-decoration:none;font-weight:700;font-size:15px">${esc(String(d.buttonText))}</a>` : ''}
</div>`
    }
    case 'testimonial-single': {
      return `<div style="padding:60px 40px;text-align:center;max-width:700px;margin:0 auto">
  <p style="font-size:22px;line-height:1.6;color:#333;font-style:italic;margin:0 0 32px">"${esc(String(d.text ?? ''))}"</p>
  <div style="display:flex;align-items:center;justify-content:center;gap:16px">
    ${d.avatar ? `<img src="${String(d.avatar)}" style="width:56px;height:56px;border-radius:50%;object-fit:cover">` : `<div style="width:56px;height:56px;border-radius:50%;background:#e8ecff;display:flex;align-items:center;justify-content:center;font-size:24px">👤</div>`}
    <div style="text-align:left">
      <div style="font-weight:700;font-size:16px">${esc(String(d.name ?? ''))}</div>
      <div style="font-size:13px;color:#888">${esc(String(d.role ?? ''))}</div>
    </div>
  </div>
</div>`
    }
    case 'video-hero': {
      const videoId = (() => {
        const url = String(d.videoUrl ?? '')
        const m = url.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/)
        return m ? m[1] : ''
      })()
      return `<div style="position:relative;min-height:480px;background:#000;display:flex;align-items:center;justify-content:center;overflow:hidden">
  ${videoId ? `<iframe src="https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0" style="position:absolute;inset:0;width:100%;height:100%;border:none;pointer-events:none" allowfullscreen allow="autoplay"></iframe>` : ''}
  <div style="position:relative;z-index:1;text-align:center;color:#fff;padding:60px 40px;background:rgba(0,0,0,0.4);width:100%">
    <h1 style="font-size:52px;font-weight:800;margin:0 0 16px;line-height:1.15">${esc(String(d.title ?? ''))}</h1>
    <p style="font-size:18px;color:rgba(255,255,255,0.8);margin:0 0 32px">${esc(String(d.subtitle ?? ''))}</p>
    ${d.buttonText ? `<a href="${String(d.buttonUrl ?? '#')}" style="display:inline-block;padding:14px 32px;background:#5b6af0;color:#fff;border-radius:8px;text-decoration:none;font-weight:700;font-size:16px">${esc(String(d.buttonText))}</a>` : ''}
  </div>
</div>`
    }
    case 'auth-form': {
      const siteId = _siteId ?? ''
      const bg = String(d.background ?? '#ffffff')
      const accent = String(d.accentColor ?? '#5b6af0')
      const formTitle = String(d.title ?? 'Вход / Регистрация')
      const formSubtitle = String(d.subtitle ?? '')
      const mode = String(d.mode ?? 'both')
      const showName = d.showName !== false
      const cabinetBg = String(d.cabinetBg ?? '#f8f9fa')
      const cabinetAccent = String(d.cabinetAccent ?? accent)
      const cabinetLayout = String(d.cabinetLayout ?? 'sidebar')
      const iconStyle = String(d.iconStyle ?? 'soft')
      const avatarShape = String(d.avatarShape ?? 'circle')
      const cabinetWelcome = String(d.cabinetWelcome ?? '')

      const sections: Array<{id: string; label: string; icon: string}> = []
      if (d.showSectionProfile !== false) sections.push({ id: 'profile', label: 'Профиль', icon: '👤' })
      if (d.showSectionOrders) sections.push({ id: 'orders', label: 'Заказы', icon: '📦' })
      if (d.showSectionBookings) sections.push({ id: 'bookings', label: 'Записи', icon: '📅' })
      if (d.showSectionDownloads) sections.push({ id: 'downloads', label: 'Загрузки', icon: '📥' })
      if (d.showSectionFavorites) sections.push({ id: 'favorites', label: 'Избранное', icon: '❤️' })
      if (d.showSectionSettings !== false) sections.push({ id: 'settings', label: 'Настройки', icon: '⚙️' })

      const sectionsJson = JSON.stringify(sections)
      const bid = 'af' + block.id.replace(/-/g, '')

      return `<div id="${bid}"></div>
<script>
(function(){
  var ROOT = document.getElementById('${bid}');
  var SITE_ID = ${JSON.stringify(siteId)};
  var ACCENT = ${JSON.stringify(cabinetAccent)};
  var BG = ${JSON.stringify(bg)};
  var CAB_BG = ${JSON.stringify(cabinetBg)};
  var LAYOUT = ${JSON.stringify(cabinetLayout)};
  var ICON_STYLE = ${JSON.stringify(iconStyle)};
  var AVATAR_SHAPE = ${JSON.stringify(avatarShape)};
  var WELCOME_MSG = ${JSON.stringify(cabinetWelcome)};
  var FORM_TITLE = ${JSON.stringify(formTitle)};
  var FORM_SUBTITLE = ${JSON.stringify(formSubtitle)};
  var SHOW_NAME = ${showName ? 'true' : 'false'};
  var MODE = ${JSON.stringify(mode)};
  var SECTIONS = ${sectionsJson};
  var API = '/api/public/site-users/' + SITE_ID;
  var SK = 'bst-site-user-' + SITE_ID;
  var currentUser = null;
  var currentSection = SECTIONS.length > 0 ? SECTIONS[0].id : 'profile';

  function getSession() { try { return JSON.parse(localStorage.getItem(SK) || 'null'); } catch(e) { return null; } }
  function setSession(u) { localStorage.setItem(SK, JSON.stringify(u)); }
  function clearSession() { localStorage.removeItem(SK); }

  function esc(s) { return String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/"/g,'&quot;'); }

  function iconBoxStyle() {
    var b = 'display:inline-flex;align-items:center;justify-content:center;width:38px;height:38px;font-size:17px;flex-shrink:0;';
    if (ICON_STYLE === 'round')   return b + 'border-radius:50%;background:' + ACCENT + ';color:#fff;';
    if (ICON_STYLE === 'square')  return b + 'border-radius:6px;background:' + ACCENT + ';color:#fff;';
    if (ICON_STYLE === 'outline') return b + 'border-radius:8px;border:2px solid ' + ACCENT + ';';
    return b + 'border-radius:10px;background:' + ACCENT + '22;';
  }

  function avatarHtml(initials) {
    if (AVATAR_SHAPE === 'none') return '';
    var r = AVATAR_SHAPE === 'circle' ? '50%' : '10px';
    return '<div style="width:64px;height:64px;border-radius:' + r + ';background:' + ACCENT + ';color:#fff;display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:700;flex-shrink:0">' + esc(initials) + '</div>';
  }

  function getInitials(name, email) {
    if (name) { var p = name.trim().split(' '); return (p.length >= 2 ? p[0][0] + p[1][0] : name.substring(0,2)).toUpperCase(); }
    return (email||'?').substring(0,2).toUpperCase();
  }

  var INP = 'width:100%;padding:10px 12px;border:1px solid #ddd;border-radius:8px;font-size:14px;box-sizing:border-box;font-family:inherit;';
  function BTN(c) { return 'padding:11px 22px;background:' + (c||ACCENT) + ';color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;'; }

  // ── Login / Register form ────────────────────────────────────────────────────
  function renderForm(tab) {
    var showTabs = MODE === 'both';
    var isReg = showTabs ? tab === 'register' : MODE === 'register';
    ROOT.innerHTML =
      '<div style="max-width:420px;margin:0 auto;padding:40px 20px;">' +
        '<div style="background:' + BG + ';border-radius:16px;padding:36px;box-shadow:0 4px 32px rgba(0,0,0,0.08);">' +
          '<h2 style="margin:0 0 8px;font-size:24px;font-weight:800;text-align:center">' + esc(FORM_TITLE) + '</h2>' +
          (FORM_SUBTITLE ? '<p style="margin:0 0 20px;color:#777;text-align:center;font-size:15px">' + esc(FORM_SUBTITLE) + '</p>' : '<div style="height:16px"></div>') +
          (showTabs ?
            '<div style="display:flex;border-bottom:2px solid #eee;margin-bottom:24px">' +
              '<button data-af-tab="login" style="flex:1;padding:10px;background:none;border:none;font-size:15px;font-weight:600;cursor:pointer;color:' + (!isReg?ACCENT:'#999') + ';border-bottom:2px solid ' + (!isReg?ACCENT:'transparent') + ';margin-bottom:-2px;font-family:inherit">Вход</button>' +
              '<button data-af-tab="register" style="flex:1;padding:10px;background:none;border:none;font-size:15px;font-weight:600;cursor:pointer;color:' + (isReg?ACCENT:'#999') + ';border-bottom:2px solid ' + (isReg?ACCENT:'transparent') + ';margin-bottom:-2px;font-family:inherit">Регистрация</button>' +
            '</div>'
          : '') +
          '<form id="af-form-${bid}" style="display:flex;flex-direction:column;gap:12px;">' +
            (isReg && SHOW_NAME ? '<input name="name" type="text" placeholder="Ваше имя" required style="' + INP + '">' : '') +
            '<input name="email" type="email" placeholder="Email" required style="' + INP + '">' +
            '<input name="password" type="password" placeholder="Пароль" required style="' + INP + '">' +
            '<button type="submit" data-af-sbtn style="' + BTN() + 'margin-top:4px;">' + (isReg?'Зарегистрироваться':'Войти') + '</button>' +
            '<div id="af-msg-${bid}" style="padding:10px 14px;border-radius:8px;font-size:14px;display:none;"></div>' +
          '</form>' +
        '</div>' +
      '</div>';
    ROOT.querySelectorAll('[data-af-tab]').forEach(function(btn) {
      btn.addEventListener('click', function() { renderForm(this.getAttribute('data-af-tab')); });
    });
    var form = document.getElementById('af-form-${bid}');
    if (form) form.addEventListener('submit', function(e) {
      e.preventDefault();
      var sbtn = ROOT.querySelector('[data-af-sbtn]');
      var msgEl = document.getElementById('af-msg-${bid}');
      var data = {}; new FormData(form).forEach(function(v,k){ data[k]=v; });
      var url = API + (isReg ? '/register' : '/login');
      if (sbtn) { sbtn.disabled = true; sbtn.textContent = '...'; }
      fetch(url, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(data)})
        .then(function(r){ return r.json().then(function(d){ return {ok:r.ok,d:d}; }); })
        .then(function(res){
          if (res.ok) { var u = res.d.user||res.d; setSession(u); renderCabinet(u); }
          else {
            if (msgEl){ msgEl.style.display='block';msgEl.style.background='#fef2f2';msgEl.style.border='1px solid #fca5a5';msgEl.style.color='#991b1b';msgEl.textContent=res.d.message||(isReg?'Ошибка регистрации':'Неверный email или пароль'); }
            if (sbtn){ sbtn.disabled=false; sbtn.textContent=isReg?'Зарегистрироваться':'Войти'; }
          }
        })
        .catch(function(){
          if (msgEl){ msgEl.style.display='block';msgEl.style.background='#fef2f2';msgEl.style.border='1px solid #fca5a5';msgEl.style.color='#991b1b';msgEl.textContent='Ошибка соединения'; }
          if (sbtn){ sbtn.disabled=false; sbtn.textContent=isReg?'Зарегистрироваться':'Войти'; }
        });
    });
  }

  // ── Section content ─────────────────────────────────────────────────────────
  function renderSectionContent(secId) {
    var el = document.getElementById('af-content-${bid}');
    if (!el || !currentUser) return;
    var u = currentUser;
    if (secId === 'profile') {
      el.innerHTML =
        '<h3 style="margin:0 0 20px;font-size:18px;font-weight:700">Мой профиль</h3>' +
        '<div style="font-size:14px;color:#888;margin-bottom:16px">Email: ' + esc(u.email) + '</div>' +
        '<form id="af-prf-${bid}" style="display:flex;flex-direction:column;gap:12px;max-width:380px;">' +
          '<div><label style="display:block;margin-bottom:4px;font-size:13px;font-weight:600;color:#555">Имя</label><input name="name" value="' + esc(u.name||'') + '" placeholder="Ваше имя" style="' + INP + '"></div>' +
          '<div><label style="display:block;margin-bottom:4px;font-size:13px;font-weight:600;color:#555">Телефон</label><input name="phone" value="' + esc(u.phone||'') + '" placeholder="+7 (999) 000-00-00" style="' + INP + '"></div>' +
          '<div style="display:flex;align-items:center;gap:12px;">' +
            '<button type="submit" style="' + BTN() + '">Сохранить</button>' +
            '<span id="af-prf-msg-${bid}" style="font-size:13px;color:green;display:none;">Сохранено!</span>' +
          '</div>' +
        '</form>';
      var prfForm = document.getElementById('af-prf-${bid}');
      if (prfForm) prfForm.addEventListener('submit', function(e) {
        e.preventDefault();
        var data = {}; new FormData(prfForm).forEach(function(v,k){ data[k]=v; });
        fetch(API+'/me/'+currentUser.id, {method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify(data)})
          .then(function(r){ return r.json(); })
          .then(function(updated){
            currentUser = Object.assign({},currentUser,{name:updated.name,phone:updated.phone});
            setSession(currentUser);
            var m = document.getElementById('af-prf-msg-${bid}');
            if (m){ m.style.display='inline'; setTimeout(function(){ m.style.display='none'; },2500); }
          }).catch(function(){});
      });
    } else if (secId === 'settings') {
      el.innerHTML =
        '<h3 style="margin:0 0 20px;font-size:18px;font-weight:700">Смена пароля</h3>' +
        '<form id="af-pw-${bid}" style="display:flex;flex-direction:column;gap:12px;max-width:360px;">' +
          '<div><label style="display:block;margin-bottom:4px;font-size:13px;font-weight:600;color:#555">Текущий пароль</label><input name="currentPassword" type="password" required placeholder="••••••••" style="' + INP + '"></div>' +
          '<div><label style="display:block;margin-bottom:4px;font-size:13px;font-weight:600;color:#555">Новый пароль</label><input name="newPassword" type="password" required placeholder="••••••••" style="' + INP + '"></div>' +
          '<div style="display:flex;align-items:center;gap:12px;">' +
            '<button type="submit" style="' + BTN() + '">Изменить</button>' +
            '<div id="af-pw-msg-${bid}" style="font-size:13px;display:none;"></div>' +
          '</div>' +
        '</form>';
      var pwForm = document.getElementById('af-pw-${bid}');
      if (pwForm) pwForm.addEventListener('submit', function(e) {
        e.preventDefault();
        var data = {}; new FormData(pwForm).forEach(function(v,k){ data[k]=v; });
        var msgEl = document.getElementById('af-pw-msg-${bid}');
        fetch(API+'/me/'+currentUser.id+'/change-password', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(data)})
          .then(function(r){ return r.json().then(function(d){ return {ok:r.ok,d:d}; }); })
          .then(function(res){
            if (msgEl){ msgEl.style.display='block'; msgEl.style.color=res.ok?'green':'#c00'; msgEl.textContent=res.ok?'Пароль изменён!':(res.d.message||'Ошибка'); if(res.ok)pwForm.reset(); setTimeout(function(){ if(msgEl)msgEl.style.display='none'; },3000); }
          }).catch(function(){});
      });
    } else {
      var sec = SECTIONS.find(function(s){ return s.id===secId; });
      el.innerHTML = '<h3 style="margin:0 0 20px;font-size:18px;font-weight:700">' + esc(sec?sec.label:secId) + '</h3><div style="color:#aaa;padding:40px 0;text-align:center;font-size:15px">Пока здесь ничего нет</div>';
    }
  }

  function switchSection(secId) {
    currentSection = secId;
    ROOT.querySelectorAll('[data-af-sec]').forEach(function(b) {
      var a = b.getAttribute('data-af-sec') === secId;
      b.style.background = a ? ACCENT+'18' : (LAYOUT==='sidebar'?'transparent':'#f8f9fa');
      b.style.fontWeight = a ? '600' : '400';
      b.style.color = a ? ACCENT : '#555';
      if (LAYOUT!=='sidebar') b.style.border = '2px solid '+(a?ACCENT:'transparent');
    });
    renderSectionContent(secId);
  }

  // ── Cabinet ─────────────────────────────────────────────────────────────────
  function renderCabinet(user) {
    currentUser = user;
    var initials = getInitials(user.name, user.email);
    var navHtml = SECTIONS.map(function(s) {
      var a = s.id === currentSection;
      if (LAYOUT === 'sidebar') {
        return '<button data-af-sec="'+s.id+'" style="display:flex;align-items:center;gap:12px;width:100%;padding:10px 14px;background:'+(a?ACCENT+'18':'transparent')+';border:none;border-radius:8px;cursor:pointer;font-size:14px;font-weight:'+(a?'600':'400')+';color:'+(a?ACCENT:'#555')+';text-align:left;font-family:inherit;">' +
          '<span style="'+iconBoxStyle()+'">'+s.icon+'</span><span>'+s.label+'</span></button>';
      } else {
        return '<button data-af-sec="'+s.id+'" style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:16px 8px;background:'+(a?ACCENT+'18':'#f8f9fa')+';border:2px solid '+(a?ACCENT:'transparent')+';border-radius:12px;cursor:pointer;font-size:13px;font-weight:'+(a?'600':'400')+';color:'+(a?ACCENT:'#555')+';font-family:inherit;width:100%;">' +
          '<span style="font-size:24px">'+s.icon+'</span><span>'+s.label+'</span></button>';
      }
    }).join('');
    var logoutHtml = LAYOUT === 'sidebar'
      ? '<button data-af-logout style="display:flex;align-items:center;gap:12px;width:100%;padding:10px 14px;background:transparent;border:none;border-radius:8px;cursor:pointer;font-size:14px;color:#e55;text-align:left;font-family:inherit;"><span style="'+iconBoxStyle()+'">🚪</span><span>Выйти</span></button>'
      : '<button data-af-logout style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:16px 8px;background:#fff5f5;border:2px solid transparent;border-radius:12px;cursor:pointer;font-size:13px;color:#e55;font-family:inherit;width:100%;"><span style="font-size:24px">🚪</span><span>Выйти</span></button>';

    var innerHtml;
    if (LAYOUT === 'sidebar') {
      innerHtml = '<div style="display:flex;gap:24px;min-height:360px;">' +
        '<nav style="width:200px;flex-shrink:0;display:flex;flex-direction:column;gap:4px;">' + navHtml +
          '<div style="margin-top:auto;padding-top:12px;border-top:1px solid #f0f0f0;">' + logoutHtml + '</div>' +
        '</nav>' +
        '<div id="af-content-${bid}" style="flex:1;min-width:0;"></div>' +
        '</div>';
    } else {
      innerHtml = '<div>' +
        '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(110px,1fr));gap:12px;margin-bottom:20px;">' + navHtml + logoutHtml + '</div>' +
        '<div id="af-content-${bid}" style="background:#fff;border-radius:12px;padding:24px;min-height:140px;"></div>' +
        '</div>';
    }

    ROOT.innerHTML =
      '<div style="padding:32px 20px;background:' + CAB_BG + ';">' +
        '<div style="max-width:860px;margin:0 auto;">' +
          '<div style="background:#fff;border-radius:16px;padding:28px;box-shadow:0 4px 32px rgba(0,0,0,0.06);">' +
            '<div style="display:flex;align-items:center;gap:16px;margin-bottom:24px;padding-bottom:20px;border-bottom:1px solid #f0f0f0;">' +
              avatarHtml(initials) +
              '<div><div style="font-size:18px;font-weight:700;">' + esc(user.name||'Пользователь') + '</div>' +
              '<div style="font-size:14px;color:#888;margin-top:2px;">' + esc(user.email) + '</div>' +
              (WELCOME_MSG ? '<div style="margin-top:8px;font-size:13px;color:'+ACCENT+';">'+esc(WELCOME_MSG)+'</div>' : '') +
              '</div>' +
            '</div>' +
            innerHtml +
          '</div>' +
        '</div>' +
      '</div>';
    ROOT.querySelectorAll('[data-af-sec]').forEach(function(btn) {
      btn.addEventListener('click', function(){ switchSection(this.getAttribute('data-af-sec')); });
    });
    ROOT.querySelectorAll('[data-af-logout]').forEach(function(btn) {
      btn.addEventListener('click', function(){
        clearSession(); currentUser=null;
        currentSection = SECTIONS.length>0 ? SECTIONS[0].id : 'profile';
        renderForm(MODE==='register'?'register':'login');
      });
    });
    renderSectionContent(currentSection);
  }

  // ── Init ────────────────────────────────────────────────────────────────────
  var session = getSession();
  if (session && session.id && SITE_ID) {
    fetch(API+'/me/'+session.id)
      .then(function(r){ return r.ok ? r.json() : Promise.reject(); })
      .then(function(u){ var m=Object.assign({},session,u); setSession(m); renderCabinet(m); })
      .catch(function(){ renderCabinet(session); });
  } else {
    renderForm(MODE==='register'?'register':'login');
  }
})();
</script>`
    }
    // ── Monolithic full-page blocks ───────────────────────────────────────────
    case 'glassmorphism-page':
      return _injectOverrides(getGlassmorphPageHtml({ brand: String(d.brand ?? 'pulse') as any }, 'preview'), d.textOverrides)
    case 'swiss-landing-page':
      return _injectOverrides(getSwissLandingPageHtml({ brand: String(d.brand ?? 'bst') as any }, 'preview'), d.textOverrides)
    case 'brutal-landing-page':
      return _injectOverrides(getBrutalLandingPageHtml({ brand: String(d.brand ?? 'acid') as any }, 'preview'), d.textOverrides)
    case 'serif-landing-page':
      return _injectOverrides(getSerifLandingPageHtml({ brand: String(d.brand ?? 'noir') as any }, 'preview'), d.textOverrides)
    case 'cinema-landing-page':
      return _injectOverrides(getCinemaLandingPageHtml({ brand: String(d.brand ?? 'noir') as any }, 'preview'), d.textOverrides)
    case 'maximal-landing-page':
      return _injectOverrides(getMaximalLandingPageHtml({ brand: String(d.brand ?? 'brutal') as any }, 'preview'), d.textOverrides)
    case 'y2k-landing-page':
      return _injectOverrides(getY2kLandingPageHtml({ brand: String(d.brand ?? 'core') as any }, 'preview'), d.textOverrides)
    case 'organic-landing-page':
      return _injectOverrides(getOrganicLandingPageHtml({ brand: String(d.brand ?? 'botanica') as any }, 'preview'), d.textOverrides)
    case 'film-landing-page':
      return _injectOverrides(getFilmLandingPageHtml({ brand: String(d.brand ?? 'noir') as any }, 'preview'), d.textOverrides)
    case 'bento-landing-page':
      return _injectOverrides(getBentoLandingPageHtml({ brand: String(d.brand ?? 'noir') as any }, 'preview'), d.textOverrides)
    case 'bento-portfolio-page':
      return _injectOverrides(getBentoPortfolioPageHtml({}, 'preview'), d.textOverrides)
    case 'cinema-portfolio-page':
      return _injectOverrides(getCinemaPortfolioPageHtml({ brand: String(d.brand ?? 'capital') as any }, 'preview'), d.textOverrides)
    case 'film-portfolio-page':
      return _injectOverrides(getFilmPortfolioPageHtml({}, 'preview'), d.textOverrides)
    case 'minimal-portfolio-page':
      return _injectOverrides(getMinimalPortfolioPageHtml({ brand: String(d.brand ?? 'acid') as any }, 'preview'), d.textOverrides)
    case 'organic-portfolio-page':
      return _injectOverrides(getOrganicPortfolioPageHtml({}, 'preview'), d.textOverrides)
    case 'swiss-portfolio-page':
      return _injectOverrides(getSwissPortfolioPageHtml({ brand: String(d.brand ?? 't1') as any }, 'preview'), d.textOverrides)
    case 'brutal-portfolio-page':
      return _injectOverrides(getBrutalPortfolioPageHtml({ brand: String(d.brand ?? 'acid') as any }, 'preview'), d.textOverrides)
    case 'immersive-portfolio-page':
      return _injectOverrides(getImmersivePortfolioPageHtml({ brand: String(d.brand ?? 'noir') as any }, 'preview'), d.textOverrides)
    case 'ai-studio-page':
      return studioPageHtml(d as Record<string, unknown>, 'site')
    case 'neo-portfolio-page':
      return _injectOverrides(getNeoPortfolioPageHtml({ brand: String(d.brand ?? 'noir') as any }, 'preview'), d.textOverrides)

    default:
      return ''
  }
}

function _injectOverrides(html: string, overrides: unknown): string {
  const map = overrides as Record<string, string> | undefined
  if (!map || !Object.keys(map).length) return html
  const script = `<script>(function(){var o=${JSON.stringify(map)};Object.keys(o).forEach(function(k){var e=document.getElementById(k)||document.querySelector('[data-bind="'+k+'"]')||document.querySelector('[data-t="'+k+'"]');if(e){if(o[k].indexOf('<')>=0)e.innerHTML=o[k];else e.textContent=o[k];}});})()</script>`
  const idx = html.lastIndexOf('</body>')
  return idx >= 0 ? html.slice(0, idx) + script + html.slice(idx) : html + script
}

// ── Section → HTML ─────────────────────────────────────────────────────────────

function sectionHtml(section: SectionState): string {
  const bg = section.background !== 'transparent' ? section.background : ''
  const style = [
    bg ? `background:${bg}` : '',
    section.backgroundSize ? `background-size:${section.backgroundSize}` : '',
    section.backgroundPosition ? `background-position:${section.backgroundPosition}` : '',
    bg ? 'background-repeat:no-repeat' : '',
    `padding-top:${section.paddingTop}px`,
    `padding-bottom:${section.paddingBottom}px`,
  ].filter(Boolean).join(';')

  const blocksHtml = section.blocks.map((b, i) => {
    const html = blockHtml(b)
    if (!html || html === '') return html
    const delays = ['', '100', '200', '300', '400', '500']
    const delay = delays[Math.min(i, delays.length - 1)]
    const hoverClass = b.hoverEffect ? ` bst-hover-${b.hoverEffect}` : ''
    return `<div data-anim="fade-up"${delay ? ` data-anim-delay="${delay}"` : ''}${hoverClass ? ` class="${hoverClass.trim()}"` : ''}>${html}</div>`
  }).join('\n')
  return `<section style="${style}">\n${blocksHtml}\n</section>`
}

// ── Interactive JS ─────────────────────────────────────────────────────────────

const interactiveJs = `
// FAQ
function bstFaqToggle(btn) {
  var item = btn.parentElement;
  var ans = item.querySelector('.bst-faq-a');
  var icon = btn.querySelector('span:last-child');
  var open = ans.style.display !== 'none';
  ans.style.display = open ? 'none' : 'block';
  icon.style.transform = open ? '' : 'rotate(45deg)';
}
// Tabs
function bstTabSwitch(btn, idx) {
  var container = btn.closest('div');
  var panels = container.parentElement.querySelectorAll('.bst-tab-panel');
  var tabs = container.querySelectorAll('.bst-tab');
  tabs.forEach(function(t,i){
    t.style.fontWeight = i===idx ? '600' : '400';
    t.style.color = i===idx ? '#5b6af0' : '#888';
    t.style.borderBottomColor = i===idx ? '#5b6af0' : 'transparent';
  });
  panels.forEach(function(p,i){ p.style.display = i===idx ? '' : 'none'; });
}
// Accordion
function bstAccToggle(btn) {
  var body = btn.nextElementSibling;
  var icon = btn.querySelector('span:last-child');
  var open = body.style.display !== 'none';
  body.style.display = open ? 'none' : 'block';
  icon.textContent = open ? '+' : '×';
}
// Countdown
(function(){
  var els = document.querySelectorAll('[data-target]');
  els.forEach(function(el){
    if (!el.id || !el.id.startsWith('bst-countdown')) return;
    var target = parseInt(el.getAttribute('data-target'));
    var vals = el.querySelectorAll('.bst-cd-val');
    function tick(){
      var diff = Math.max(0, target - Date.now());
      var d = Math.floor(diff/86400000);
      var h = Math.floor((diff%86400000)/3600000);
      var m = Math.floor((diff%3600000)/60000);
      var s = Math.floor((diff%60000)/1000);
      var parts = [d,h,m,s];
      vals.forEach(function(v,i){ v.textContent = String(parts[i]).padStart(2,'0'); });
      if (diff > 0) setTimeout(tick, 1000);
    }
    tick();
  });
})();
// Counter animation
(function(){
  var counters = document.querySelectorAll('.bst-counter');
  counters.forEach(function(el){
    var target = parseInt(el.getAttribute('data-target')) || 0;
    var dur = parseInt(el.getAttribute('data-duration')) || 2000;
    var prefix = el.textContent.replace(/[\\d,]+.*/, '');
    var suffix = el.textContent.replace(/.*?([^\\d,].*)$/, '$1').replace(/^[\\d,]+/, '');
    var start = Date.now();
    function tick(){
      var p = Math.min(1, (Date.now()-start)/dur);
      var eased = 1 - Math.pow(1-p, 3);
      var val = Math.round(target * eased);
      el.textContent = prefix + val.toLocaleString('ru-RU') + suffix;
      if (p < 1) requestAnimationFrame(tick);
    }
    tick();
  });
})();
// Form submit
function bstFormSubmit(e, url, form) {
  e.preventDefault();
  var btn = form.querySelector('button[type=submit]');
  var msg = form.querySelector('.bst-form-msg');
  var origText = btn.textContent;
  btn.disabled = true; btn.textContent = '...';
  var data = {};
  new FormData(form).forEach(function(v, k){ data[k] = v; });
  fetch(url, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({data:data})})
    .then(function(r){ return r.ok ? 'ok' : 'err'; })
    .catch(function(){ return 'err'; })
    .then(function(status){
      if (msg) {
        msg.style.display = 'block';
        msg.style.background = status === 'ok' ? '#f0fdf4' : '#fef2f2';
        msg.style.border = '1px solid ' + (status === 'ok' ? '#86efac' : '#fca5a5');
        msg.style.color = status === 'ok' ? '#166534' : '#991b1b';
        msg.textContent = status === 'ok' ? 'Спасибо! Сообщение отправлено.' : 'Произошла ошибка. Попробуйте позже.';
      }
      if (status === 'ok') form.reset();
      btn.disabled = false; btn.textContent = origText;
    });
}
// Slider
function bstSlider(dot, idx) {
  var wrap = dot.closest('.bst-slider');
  var slides = wrap.querySelectorAll('.bst-slide');
  var dots = wrap.querySelectorAll('.bst-dot');
  slides.forEach(function(s,i){ s.style.display = i===idx ? 'flex' : 'none'; });
  dots.forEach(function(d,i){ d.style.background = i===idx ? '#fff' : 'rgba(255,255,255,0.4)'; });
}
// Before/After slider
(function(){
  document.querySelectorAll('.bst-ba').forEach(function(el){
    var before = el.querySelector('.bst-ba-before');
    var handle = el.querySelector('.bst-ba-handle');
    var pos = parseFloat(el.getAttribute('data-pos')) || 50;
    function setPos(p){
      pos = Math.min(100, Math.max(0, p));
      before.style.clipPath = 'inset(0 '+(100-pos)+'% 0 0)';
      handle.style.left = pos+'%';
    }
    setPos(pos);
    var dragging = false;
    el.addEventListener('mousedown', function(e){ dragging=true; setPos((e.clientX-el.getBoundingClientRect().left)/el.offsetWidth*100); });
    el.addEventListener('mousemove', function(e){ if(dragging) setPos((e.clientX-el.getBoundingClientRect().left)/el.offsetWidth*100); });
    el.addEventListener('mouseup', function(){ dragging=false; });
    el.addEventListener('mouseleave', function(){ dragging=false; });
    el.addEventListener('touchstart', function(e){ dragging=true; }, {passive:true});
    el.addEventListener('touchmove', function(e){ if(dragging){ var t=e.touches[0]; setPos((t.clientX-el.getBoundingClientRect().left)/el.offsetWidth*100); } }, {passive:true});
    el.addEventListener('touchend', function(){ dragging=false; });
  });
})();
`

// ── Main export ────────────────────────────────────────────────────────────────

export function exportToHTML(page: PageState, settings?: SiteSettings, siteId?: string): string {
  _pageContext = page
  _siteId = siteId ?? null
  const sectionsHtml = page.sections.map(sectionHtml).join('\n\n')
  _pageContext = null
  _siteId = null
  const slug = page.slug || '/'
  const siteName = settings?.name || 'BST Site'
  const canonical = `https://${slug.replace(/^\//, '') || 'my-site'}.mybst.ru`
  const robotsMeta = page.robotsIndex === false ? 'noindex,nofollow' : 'index,follow'

  const gaScript = settings?.gaId
    ? `  <!-- Google Analytics 4 -->
  <script async src="https://www.googletagmanager.com/gtag/js?id=${settings.gaId}"></script>
  <script>window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js',new Date());gtag('config','${settings.gaId}');</script>`
    : ''

  const ymScript = settings?.ymId
    ? `  <!-- Яндекс.Метрика -->
  <script type="text/javascript">(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};m[i].l=1*new Date();for(var j=0;j<document.scripts.length;j++){if(document.scripts[j].src===r){return;}}k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})(window,document,"script","https://mc.yandex.ru/metrika/tag.js","ym");ym(${settings.ymId},"init",{clickmap:true,trackLinks:true,accurateTrackBounce:true});</script>
  <noscript><div><img src="https://mc.yandex.ru/watch/${settings.ymId}" style="position:absolute;left:-9999px;" alt=""/></div></noscript>`
    : ''

  return `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${esc(page.seoTitle || page.title)}</title>
  <meta name="robots" content="${robotsMeta}">
  ${page.seoDesc ? `<meta name="description" content="${esc(page.seoDesc)}">` : ''}
  <meta property="og:title" content="${esc(page.seoTitle || page.title)}">
  <meta property="og:site_name" content="${esc(siteName)}">
  ${page.seoDesc ? `<meta property="og:description" content="${esc(page.seoDesc)}">` : ''}
  ${page.ogImage ? `<meta property="og:image" content="${page.ogImage}">` : ''}
  <meta property="og:type" content="website">
  <link rel="canonical" href="${canonical}">
  ${settings?.favicon ? `<link rel="icon" href="${settings.favicon}">` : ''}
  ${settings?.customHeadHtml ?? ''}
${gaScript}
${ymScript}
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    html { scroll-behavior: smooth; }
    body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: #1a1a1a; }
    img { max-width: 100%; display: block; }
    a { color: inherit; }
    button { font-family: inherit; }
    input, textarea, select { font-family: inherit; outline: none; }
    @media (max-width: 768px) {
      body{overflow-x:hidden}
      [style*="grid-template-columns:repeat(2,1fr)"],[style*="grid-template-columns:repeat(3,1fr)"],[style*="grid-template-columns:repeat(4,1fr)"],[style*="grid-template-columns:repeat(5,1fr)"],[style*="grid-template-columns:repeat(6,1fr)"],[style*="grid-template-columns:1fr 1fr"],[style*="grid-template-columns:2fr 3fr"],[style*="grid-template-columns:3fr 2fr"],[style*="grid-template-columns:1fr 2fr"],[style*="grid-template-columns:2fr 1fr"],[style*="grid-template-columns:2fr 1fr 1fr"],[style*="grid-template-columns:2fr repeat"],[style*="grid-template-columns:auto repeat"]{grid-template-columns:1fr!important}
      [style*="padding:100px 40px"]{padding:48px 16px!important}
      [style*="padding:80px 40px"]{padding:44px 16px!important}
      [style*="padding:64px 40px"]{padding:36px 16px!important}
      [style*="padding:60px 40px"]{padding:36px 16px!important}
      [style*="padding:48px 40px"]{padding:28px 16px!important}
      [style*="padding:40px 40px"]{padding:24px 16px!important}
      [style*="padding:32px 40px"]{padding:20px 16px!important}
      [style*="padding:28px 40px"]{padding:18px 16px!important}
      [style*="padding:24px 40px"]{padding:16px 14px!important}
      [style*="padding:80px 24px"]{padding:44px 14px!important}
      [style*="padding:64px 24px"]{padding:36px 14px!important}
      [style*="padding:60px 24px"]{padding:36px 14px!important}
      [style*="padding:48px 24px"]{padding:28px 14px!important}
      [style*="padding:40px 24px"]{padding:24px 14px!important}
      [style*="font-size:72px"],[style*="font-size:80px"],[style*="font-size:96px"]{font-size:32px!important;letter-spacing:-0.5px!important}
      [style*="font-size:52px"],[style*="font-size:56px"],[style*="font-size:64px"]{font-size:30px!important;letter-spacing:-0.5px!important}
      [style*="font-size:48px"],[style*="font-size:44px"]{font-size:26px!important}
      [style*="font-size:40px"],[style*="font-size:36px"]{font-size:24px!important}
      .bst-flex-split{flex-direction:column!important}
      .bst-flex-split > div{min-height:200px!important;width:100%!important}
      [style*="min-height:100vh"],[style*="min-height:90vh"]{min-height:60vh!important}
      [style*="columns:2"],[style*="columns:3"],[style*="columns:4"],[style*="columns:5"]{columns:1!important}
      .bst-nav-links{display:none!important}
      .bst-nav-burger{display:flex!important}
    }
    @media (min-width: 769px) and (max-width: 1024px) {
      [style*="grid-template-columns:repeat(3,1fr)"],[style*="grid-template-columns:repeat(4,1fr)"],[style*="grid-template-columns:repeat(5,1fr)"],[style*="grid-template-columns:repeat(6,1fr)"]{grid-template-columns:repeat(2,1fr)!important}
    }
    /* Scroll animations */
    [data-anim] { opacity: 0; transition: opacity 0.6s ease, transform 0.6s ease; }
    [data-anim="fade"] { }
    [data-anim="fade-up"] { transform: translateY(32px); }
    [data-anim="fade-down"] { transform: translateY(-32px); }
    [data-anim="fade-left"] { transform: translateX(40px); }
    [data-anim="fade-right"] { transform: translateX(-40px); }
    [data-anim="zoom"] { transform: scale(0.92); }
    [data-anim].bst-visible { opacity: 1; transform: none !important; }
    [data-anim-delay="100"] { transition-delay: 0.1s; }
    [data-anim-delay="200"] { transition-delay: 0.2s; }
    [data-anim-delay="300"] { transition-delay: 0.3s; }
    [data-anim-delay="400"] { transition-delay: 0.4s; }
    [data-anim-delay="500"] { transition-delay: 0.5s; }
    /* Hover effects */
    .bst-hover-lift,.bst-hover-scale,.bst-hover-glow,.bst-hover-fade { transition: transform 0.22s ease, box-shadow 0.22s ease, opacity 0.22s ease; }
    .bst-hover-lift:hover  { transform: translateY(-6px); box-shadow: 0 12px 32px rgba(0,0,0,0.18); }
    .bst-hover-scale:hover { transform: scale(1.03); }
    .bst-hover-glow:hover  { box-shadow: 0 0 28px rgba(91,106,240,0.45); }
    .bst-hover-fade:hover  { opacity: 0.55; }
    .bst-hover-shake:hover { animation: bst-shake 0.5s ease; }
    @keyframes bst-shake {
      0%,100% { transform: translateX(0); }
      15% { transform: translateX(-6px) rotate(-0.5deg); }
      30% { transform: translateX(6px) rotate(0.5deg); }
      50% { transform: translateX(-4px); }
      70% { transform: translateX(4px); }
      85% { transform: translateX(-2px); }
    }
  </style>
</head>
<body>
${sectionsHtml}
<script>
${interactiveJs}
// Scroll animations
(function(){
  var obs = new IntersectionObserver(function(entries){
    entries.forEach(function(e){
      if(e.isIntersecting){ e.target.classList.add('bst-visible'); obs.unobserve(e.target); }
    });
  },{threshold:0.12});
  document.querySelectorAll('[data-anim]').forEach(function(el){ obs.observe(el); });
})();
</script>
</body>
</html>`
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function esc(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}
